/**
 * AITab — Top-level "AI" admin tab (Feb 2026 restructure).
 *
 * Purpose:
 *   Single home for all AI-related admin surfaces. Two top-level sub-tabs:
 *     - Oscar (guest-facing concierge)
 *     - Milo  (admin/operator assistant — placeholder until Phase 5)
 *
 *   Each sub-tab houses its own nested admin surfaces. Today only Oscar →
 *   Conversations is operational; the rest are polished placeholders that
 *   will fill in as Phases 2/3/5/6+ ship.
 *
 * What this currently does:
 *   - Renders the AI top-level shell with Oscar/Milo sub-tab nav.
 *   - Inside Oscar: the existing Conversations log viewer + 3 Phase 3
 *     placeholders (Unknown Questions · KB Facts · Settings).
 *   - Inside Milo: 4 Phase 5+ placeholders (Console · Runbooks · Action
 *     Log · Settings).
 *   - NO AI behavior, NO writes. Pure presentational + read-only Oscar log.
 *
 * What this is NOT:
 *   - Not the Oscar chat widget (lives in /app/frontend/src/components/OscarButton.jsx).
 *   - Not the legacy Web Comments page (reachable via "Guest Mailbox" sidebar item).
 *
 * Locked under PROTECTED_GUEST_FACING.md? No — admin-only surface.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, MessageSquareText, AlertOctagon, BookOpen, Sliders, Lock, RefreshCw, ChevronRight, ChevronDown, Bot, Terminal, ScrollText, ListChecks, Search, Users, Layers, MessageCircle, HelpCircle, Shield } from 'lucide-react';
import { adminAuthHeader, getAdminToken } from '../../data/api';
import AdminPageHeader from './AdminPageHeader';
import MiloRunbooksPanel from './MiloRunbooksPanel';
import MiloConsolePanel from './MiloConsolePanel';
import MiloActionLogPanel from './MiloActionLogPanel';
import MiloSettingsPanel from './MiloSettingsPanel';
// Pass 6 / M17 — read-only matrix surfacing system_rules constants
// (boundary contracts + tier vocabulary + L2 read-only contract +
// operator-tier separation contract). Single new admin sub-tab.
import MiloBoundariesPanel from './MiloBoundariesPanel';
// Pass 7 / O2 — Operator-curated general guidance CRUD surface.
import OscarGuidancePanel from './OscarGuidancePanel';

const API = process.env.REACT_APP_BACKEND_URL;

const GOLD     = '#C6A75A';
const GOLD_DK  = '#8E7A2C';
const DARK     = '#1B1812';
// Beige removal pass (Feb 2026): admin surfaces use white + cool-gray.
// Gold remains as restrained accent on borders/badges/active states only.
const SURFACE  = '#FFFFFF';                  // was '#FFFCF7' (warm cream)
const BORDER   = 'rgba(0,0,0,0.10)';         // was warm gold border on every fill
const BORDER_GOLD = 'rgba(198,167,90,0.32)'; // intentional gold accent borders only

// Corrective pre-apply pass — human-readable classifier label display.
// Backend stores raw labels (v2_kb_and_guidance, v2_off_topic, etc.); the
// admin UI shows them in the Activity & Health panel and in conversation
// row chips. Without this mapping, the operator reads raw underscore-cased
// strings. With it, the labels read as plain English.
const CLASSIFIER_LABEL_DISPLAY = {
  // V2 happy paths
  'v2_on_topic':                          'On topic',
  'v2_kb_hit':                            'KB confirmed',
  'v2_guidance_only':                     'Guidance only',
  'v2_kb_and_guidance':                   'KB + Guidance',
  // V2 escalate paths
  'v2_escalate':                          'Escalated',
  'v2_kb_resolved_escalate':              'KB resolved · was escalate',
  'v2_guidance_resolved_escalate':        'Guidance resolved · was escalate',
  'v2_kb_and_guidance_resolved_escalate': 'KB + Guidance resolved · was escalate',
  // V2 abuse / off-topic paths
  'v2_off_topic':                         'Off topic',
  'v2_session_capped':                    'Session capped',
  'v2_narrowed':                          'Narrowed',
  'v2_canned':                            'Canned (no LLM key)',
  'v2_canned_fallback':                   'Canned fallback (LLM error)',
  'v2_kb_resolved_deflection':            'KB resolved · was deflection',
  // Legacy paths (Phase 1)
  'legacy_canned':                        'Legacy · canned',
  'legacy_llm':                           'Legacy · LLM',
  'legacy_canned_fallback':               'Legacy · canned fallback',
  // Pass 6 separation
  'v2_off_topic_legacy':                  'Off topic (legacy gate)',
};
const displayClassifierLabel = (raw) => CLASSIFIER_LABEL_DISPLAY[raw] || (raw || 'Unclassified');
const MUTED    = '#5C5C66';                  // was '#7A6A40' (warm muted)
const SOFT_BG  = 'rgba(0,0,0,0.025)';        // was warm gold tint

const OSCAR_SUBTABS = [
  {
    key: 'conversations',
    label: 'Conversations',
    Icon: MessageSquareText,
    blurb: "Every guest exchange with Oscar, classified by topic, with knowledge-source attribution. Drill into a conversation to see what Oscar knew, didn't know, and where it came from.",
    arriving: 'Phase 1 (live)',
  },
  {
    key: 'kb',
    label: 'Knowledge Base',
    Icon: BookOpen,
    blurb: "The guest-safe knowledge base. Curate the answers Oscar is allowed to use — gift guidance, hotels, attire, dietary, accessibility, local guide. Unknown questions from guests will surface here for review and approval.",
    arriving: 'Phase 2C (live)',
  },
  // Pass 7 / O2 — Operator-curated general guidance. Sibling to the KB
  // but with a different trust posture: KB facts are host-confirmed;
  // guidance is admin context for travel/lodging/local/gift/etc. that
  // Oscar surfaces when the KB has no answer.
  {
    key: 'guidance',
    label: 'General Guidance',
    Icon: Layers,
    blurb: "Operator-curated guidance for travel, lodging, gifts, parking, dress code, and other common guest topics. Used as secondary grounding when the KB has no host-confirmed fact. Oscar treats these as context, not as confirmed facts.",
    arriving: 'Phase 7',
  },
  {
    key: 'unknown',
    label: 'Unknown Questions',
    Icon: HelpCircle,
    blurb: "Questions Oscar escalated because no published KB fact answered them. Promote good ones into the KB; dismiss the rest. Recurring questions surface here with their occurrence count.",
    arriving: 'Phase 3B (live)',
  },
  {
    key: 'health',
    label: 'Activity & Health',
    Icon: AlertOctagon,
    blurb: "Usage volume, abuse signals, classifier breakdown, and cost-to-date. The summary KPI card on the Dashboard rolls up from this surface.",
    arriving: 'Phase 3',
  },
  {
    key: 'settings',
    label: 'Settings',
    Icon: Sliders,
    blurb: "Oscar Mode, persona controls, soft-budget tracking, abuse heuristics, retention controls, allowed internet categories. Operator-only configuration.",
    arriving: 'Phase 1 (partial)',
  },
];

const MILO_SUBTABS = [
  {
    key: 'console',
    label: 'Console',
    Icon: Terminal,
    blurb: "Conversational admin assistant. Ask operational questions, get grounded answers, propose Tier-A safe actions with explicit confirmation. Read-only first — execution arrives in a later phase.",
    arriving: 'Phase 5',
  },
  {
    key: 'runbooks',
    label: 'Runbooks',
    Icon: ScrollText,
    blurb: "Structured admin documentation Milo grounds against. One runbook per tab, plus operational playbooks. Operator-curated, not free-form text.",
    arriving: 'Phase 5',
  },
  {
    key: 'actionlog',
    label: 'Action Log',
    Icon: ListChecks,
    blurb: "Every Milo-initiated action with full plan, confirmation timestamp, executor, and undo payload. Audit trail for everything Milo does on your behalf.",
    arriving: 'Phase 6+',
  },
  {
    key: 'settings',
    label: 'Settings',
    Icon: Sliders,
    blurb: "Tier permissions, runbook scope, action allow-lists, template-matching governance. Operator-only configuration.",
    arriving: 'Phase 5',
  },
  // Pass 6 / M17 — Boundaries surface. Read-only matrix that shows
  // exactly what each AI system is allowed to do, plus the L0/L1/L2/L3
  // tier contracts and the operator-tier separation policy.
  {
    key: 'boundaries',
    label: 'Boundaries',
    Icon: Shield,
    blurb: "Action boundary matrix. Read-only declaration of which capabilities each AI system has, the action-tier vocabulary, and the operator/customer separation policy. Sourced live from /api/system-rules.",
    arriving: 'Phase 6',
  },
];

const StatusBadge = ({ children, tone = 'gold' }) => {
  const palette = tone === 'gold'
    ? { bg: 'rgba(198,167,90,0.12)', fg: GOLD_DK, br: 'rgba(198,167,90,0.30)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED,   br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}` }}>
      {children}
    </span>
  );
};

// ─── Oscar Live Mode block — primary system control ─────────────────────────
// Sits at the top of the Oscar area, above the subtab nav, visible across all
// 4 Oscar subtabs. This is the master operating switch for guest-facing Oscar.
// Visually distinct from the Conversations Mode filter (which only filters
// past logs). Designed to feel like a circuit breaker, not a buried setting.
const OscarLiveModeBlock = ({ oscarMode, onModeChange }) => {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const setMode = async (target) => {
    if (target !== 'basic' && target !== 'enhanced') return;
    if (oscarMode === target) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/oscar/mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...adminAuthHeader(),
        },
        body: JSON.stringify({ mode: target }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d?.mode) onModeChange(d.mode);
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const isBasic = oscarMode === 'basic';

  return (
    <div data-testid="oscar-live-mode-block"
      style={{
        position: 'relative',
        border: `1.5px solid ${BORDER_GOLD}`,
        borderRadius: 14,
        background: '#FFFFFF',
        padding: '18px 22px',
        marginBottom: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: '0 4px 18px rgba(20,15,5,0.05), inset 0 1px 0 rgba(255,255,255,0.8)',
      }}
    >
      {/* Top edge: refined gold rule — restrained, premium */}
      <div aria-hidden style={{
        position: 'absolute', top: -1.5, left: -1.5, right: -1.5, height: 2,
        borderTopLeftRadius: 14, borderTopRightRadius: 14,
        background: `linear-gradient(90deg, rgba(198,167,90,0.0) 0%, ${GOLD} 50%, rgba(198,167,90,0.0) 100%)`,
        opacity: 0.85,
      }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap' }}>
        {/* Left — label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {/* Soft breathing live dot — restrained, premium */}
          <span style={{ position: 'relative', width: 9, height: 9, flexShrink: 0 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%',
              background: '#1E7344',
              animation: 'oscarLivePulse 3.6s ease-in-out infinite' }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '1.4px',
                textTransform: 'uppercase', color: '#1E7344',
                padding: '2px 8px', borderRadius: 99,
                background: 'rgba(30,115,68,0.10)',
                border: '1px solid rgba(30,115,68,0.30)' }}>
                Live
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', color: MUTED }}>
                Master Control
              </span>
            </div>
            <h3 style={{ margin: 0, fontFamily: 'Playfair Display, Georgia, serif',
              fontSize: 22, fontWeight: 700, color: DARK, letterSpacing: '-0.2px',
              lineHeight: 1.1 }}>
              Oscar Operating Mode
            </h3>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 4, lineHeight: 1.5,
              maxWidth: 460 }}>
              Switching takes effect on the very next guest chat. This is the live
              control — not a filter.
            </div>
          </div>
        </div>

        {/* Right — segmented master switch */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.2px',
            textTransform: 'uppercase', color: MUTED }}>
            Currently active
          </span>
          <div role="radiogroup" data-testid="oscar-mode-segment"
            aria-label="Oscar live operating mode"
            style={{ display: 'inline-flex', borderRadius: 999,
              border: `1px solid ${BORDER_GOLD}`,
              background: '#FAFAFB', overflow: 'hidden',
              padding: 3, gap: 0 }}>
            <button
              role="radio"
              aria-checked={isBasic}
              onClick={() => setMode('basic')}
              disabled={saving}
              data-testid="oscar-mode-basic"
              style={{ padding: '10px 20px', border: 'none', borderRadius: 999,
                background: isBasic
                  ? `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`
                  : 'transparent',
                color: isBasic ? '#1B1812' : MUTED,
                fontWeight: 800, fontSize: 12, letterSpacing: '0.8px',
                textTransform: 'uppercase', cursor: saving ? 'wait' : 'pointer',
                transition: 'all 0.18s ease',
                boxShadow: isBasic ? '0 2px 8px rgba(198,167,90,0.30)' : 'none',
                display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Basic
              {isBasic && (
                <span data-testid="oscar-mode-basic-active-pill"
                  style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px',
                    borderRadius: 99,
                    background: 'rgba(27,24,18,0.08)',
                    color: '#1B1812',
                    border: '1px solid rgba(27,24,18,0.16)',
                    letterSpacing: '0.6px' }}>
                  Active
                </span>
              )}
            </button>
            <button
              role="radio"
              aria-checked={!isBasic}
              onClick={() => setMode('enhanced')}
              disabled={saving}
              title="Enhanced — luxury concierge persona, relevance gate, KB injection, structured logging."
              data-testid="oscar-mode-enhanced"
              style={{ padding: '10px 20px', border: 'none', borderRadius: 999,
                background: !isBasic
                  ? 'linear-gradient(135deg, #1E7344, #145B33)'
                  : 'transparent',
                color: !isBasic ? '#FFFFFF' : MUTED,
                opacity: 1,
                fontWeight: 800, fontSize: 12, letterSpacing: '0.8px',
                textTransform: 'uppercase', cursor: saving ? 'wait' : 'pointer',
                transition: 'all 0.18s ease',
                boxShadow: !isBasic ? '0 2px 8px rgba(30,115,68,0.30)' : 'none',
                display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Enhanced
              {!isBasic && (
                <span data-testid="oscar-mode-enhanced-active-pill"
                  style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px',
                    borderRadius: 99,
                    background: 'rgba(255,255,255,0.18)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.32)',
                    letterSpacing: '0.6px' }}>
                  Active
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom strip: explicit disambiguation from the log filter */}
      <div data-testid="oscar-live-mode-disambiguation"
        style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', borderRadius: 10,
          background: 'rgba(198,167,90,0.05)',
          border: `1px solid ${BORDER_GOLD}` }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.7px',
          textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99,
          background: 'rgba(198,167,90,0.14)', color: GOLD_DK,
          border: `1px solid ${BORDER_GOLD}` }}>
          Note
        </span>
        <span style={{ fontSize: 11, color: MUTED, lineHeight: 1.5 }}>
          The <strong style={{ color: DARK }}>Mode filter</strong> in Conversations only filters past
          log entries. It does <em>not</em> change Oscar's live behavior. Use this
          block as the master switch.
        </span>
      </div>

      {error && (
        <div data-testid="oscar-mode-error" style={{ fontSize: 12, color: '#B23A48' }}>
          Failed to update: {error}
        </div>
      )}

      <style>{`
        @keyframes oscarLivePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(30,115,68,0); }
          50%      { box-shadow: 0 0 0 4px rgba(30,115,68,0.16); }
        }
      `}</style>
    </div>
  );
};

const SubtabPanel = ({ subtab }) => {
  const { Icon } = subtab;
  return (
    <div data-testid={`oscar-subtab-panel-${subtab.key}`}
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '32px 28px', minHeight: 220,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 12 }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%',
        background: SOFT_BG, display: 'flex', alignItems: 'center',
        justifyContent: 'center', border: `1px solid ${BORDER}` }}>
        <Icon size={24} color={GOLD} strokeWidth={1.5} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
          {subtab.label}
        </h3>
        <StatusBadge tone="muted">Arriving {subtab.arriving}</StatusBadge>
      </div>
      <p style={{ margin: 0, maxWidth: 520, fontSize: 13, lineHeight: 1.65,
        color: MUTED }}>
        {subtab.blurb}
      </p>
      <div style={{ marginTop: 6, fontSize: 11, color: MUTED, fontStyle: 'italic',
        display: 'flex', alignItems: 'center', gap: 6 }}>
        <Lock size={11} /> No live data attached yet — foundation layer only.
      </div>
    </div>
  );
};

// ─── Conversations subtab — read-only Oscar log viewer (Feb 2026) ───────────
// Phase 1 observability. Admin-only. Reads from /api/oscar/logs which sources
// the oscar_logs collection populated by the legacy /api/chat path.
// No moderation, no edits, no exports. Pure read-only window.
const FLAG_RED = '#B23A48';
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};
const truncate = (s, n) => (s && s.length > n) ? `${s.slice(0, n)}…` : (s || '');

const ConversationsPanel = () => {
  const [logs, setLogs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [viewMode, setViewMode]       = useState('sessions'); // 'sessions' | 'feed'
  const [search, setSearch]           = useState('');
  const [modeFilter, setModeFilter]   = useState('all');     // 'all' | 'basic' | 'enhanced'
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [expandedFeedId, setExpandedFeedId]     = useState(null);

  const adminSession = getAdminToken();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/oscar/logs?limit=200`, {
        headers: { Authorization: `Bearer ${adminSession}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLogs(Array.isArray(d.logs) ? d.logs : []);
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
  }, [adminSession]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Filter by search (matches across question + reply, case-insensitive)
  // and mode (Feb 2026 reset).
  const searchLower = search.trim().toLowerCase();
  const matchesSearch = (l) => !searchLower
    || (l.user_msg || '').toLowerCase().includes(searchLower)
    || (l.assistant_msg || '').toLowerCase().includes(searchLower);
  const matchesMode = (l) => modeFilter === 'all' || (l.mode || 'basic') === modeFilter;
  const filtered = logs.filter(l => matchesSearch(l) && matchesMode(l));

  // Group by session_id for Sessions mode (newest session first by latest turn).
  const threads = (() => {
    const map = new Map();
    for (const log of filtered) {
      const sid = log.session_id || 'anonymous';
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid).push(log);
    }
    // Each thread: turns sorted oldest→newest within session for natural reading.
    const arr = Array.from(map.entries()).map(([sid, turns]) => {
      const sorted = [...turns].sort((a, b) =>
        (a.created_at || '').localeCompare(b.created_at || ''));
      const labelCounts = {};
      const modeCounts = {};
      let flaggedCount = 0;
      for (const t of sorted) {
        const lbl = t.classifier_label || 'unclassified';
        labelCounts[lbl] = (labelCounts[lbl] || 0) + 1;
        const m = t.mode || 'basic';
        modeCounts[m] = (modeCounts[m] || 0) + 1;
        if (t.flagged_reason) flaggedCount += 1;
      }
      const linked = sorted.some(t => t.party_id || t.guest_id);
      // Dominant mode = most-recorded mode in this thread (defaults to 'basic').
      const dominantMode = Object.entries(modeCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'basic';
      return {
        sessionId: sid,
        turns: sorted,
        firstAt:  sorted[0]?.created_at,
        lastAt:   sorted[sorted.length - 1]?.created_at,
        labelCounts,
        modeCounts,
        dominantMode,
        flaggedCount,
        linked,
      };
    });
    // Newest thread first by lastAt.
    arr.sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''));
    return arr;
  })();

  const linkedCount = threads.filter(t => t.linked).length;
  const selectedThread = threads.find(t => t.sessionId === selectedThreadId);

  // Auto-select first thread on first load (when viewing Sessions and no
  // selection yet). Avoids the empty right-pane on initial paint.
  useEffect(() => {
    if (viewMode === 'sessions' && !selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].sessionId);
    }
  }, [viewMode, selectedThreadId, threads]);

  // ─── Glass tokens (scoped to this panel) — Feb 2026 beige removal ─────
  // White-based surfaces with cool-gray differentiation. Gold only on
  // restrained accents (borders, active states, pills).
  const G_PANEL_BG     = 'rgba(255, 255, 255, 0.72)';   // white glass
  const G_PANEL_BORDER = 'rgba(0, 0, 0, 0.08)';         // cool-gray border
  const G_PANEL_SHADOW = '0 1px 0 rgba(255,255,255,0.55) inset, 0 10px 36px rgba(15,15,20,0.06)';
  const G_BLUR         = 'blur(18px) saturate(140%)';
  const G_RAIL_BG      = 'rgba(248, 248, 250, 0.78)';   // light cool-gray glass
  const G_INPUT_BG     = 'rgba(255, 255, 255, 0.92)';   // pure white input

  // Glass background page tint behind everything (subtle warm gradient).
  const pageTint = {
    position: 'relative',
    minHeight: 540,
  };

  return (
    <div data-testid="oscar-subtab-panel-conversations" style={pageTint}>
      {/* Decorative wash + grain (very subtle; pointer-events disabled).
          Beige removal (Feb 2026): cool-gray instead of warm gold radials. */}
      <div aria-hidden style={{ position: 'absolute', inset: -8, borderRadius: 18,
        background: 'radial-gradient(120% 80% at 0% 0%, rgba(0,0,0,0.025) 0%, transparent 55%), radial-gradient(120% 80% at 100% 100%, rgba(0,0,0,0.018) 0%, transparent 60%)',
        pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1,
        display: 'grid', gridTemplateColumns: '220px minmax(360px, 1fr) minmax(360px, 1.1fr)',
        gap: 14, alignItems: 'start' }}>

        {/* ── Left rail — view mode + search + future guest filter ─── */}
        <div data-testid="oscar-conv-left-rail"
          style={{ position: 'sticky', top: 0,
            background: G_RAIL_BG, backdropFilter: G_BLUR, WebkitBackdropFilter: G_BLUR,
            border: `1px solid ${G_PANEL_BORDER}`, borderRadius: 14,
            padding: '16px 14px', boxShadow: G_PANEL_SHADOW }}>

          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 8 }}>View</div>
          <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            {[
              { id: 'sessions', label: 'Sessions', Icon: Layers, hint: 'Threaded by visit' },
              { id: 'feed',     label: 'Feed',     Icon: MessageCircle, hint: 'Chronological log' },
            ].map(opt => {
              const active = viewMode === opt.id;
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.id}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setViewMode(opt.id)}
                  data-testid={`oscar-view-${opt.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px', borderRadius: 10,
                    background: active ? '#FFFFFF' : 'transparent',
                    border: `1px solid ${active ? GOLD : 'transparent'}`,
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                    boxShadow: active ? '0 1px 6px rgba(198,167,90,0.18)' : 'none' }}
                >
                  <Icon size={14} color={active ? GOLD_DK : MUTED} strokeWidth={1.8} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600,
                      color: active ? DARK : MUTED }}>{opt.label}</div>
                    <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>{opt.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>Search</div>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: MUTED }} />
            <input
              data-testid="oscar-conv-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Find in question or reply…"
              style={{ width: '100%', padding: '8px 10px 8px 28px', borderRadius: 10,
                border: `1px solid ${G_PANEL_BORDER}`, background: G_INPUT_BG,
                fontSize: 12, color: DARK, outline: 'none',
                fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Filter logs by mode</div>
          <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic',
            lineHeight: 1.45, marginBottom: 8 }}>
            Filters past conversations only — does not change the live mode.
          </div>
          <div data-testid="oscar-conv-mode-filter"
            style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            {[
              { id: 'all',      label: 'All',      enabled: true },
              { id: 'basic',    label: 'Basic',    enabled: true },
              { id: 'enhanced', label: 'Enhanced', enabled: false, hint: 'Coming Soon' },
            ].map(opt => {
              const active = modeFilter === opt.id && opt.enabled;
              return (
                <button
                  key={opt.id}
                  onClick={() => opt.enabled && setModeFilter(opt.id)}
                  disabled={!opt.enabled}
                  data-testid={`oscar-conv-mode-${opt.id}`}
                  title={opt.hint || ''}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '7px 10px', borderRadius: 10,
                    background: active ? '#FFFFFF' : 'transparent',
                    border: `1px solid ${active ? GOLD : 'transparent'}`,
                    cursor: opt.enabled ? 'pointer' : 'not-allowed',
                    opacity: opt.enabled ? 1 : 0.55,
                    boxShadow: active ? '0 1px 6px rgba(198,167,90,0.18)' : 'none' }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: active ? DARK : MUTED }}>{opt.label}</span>
                  {opt.hint && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 99,
                      background: 'rgba(0,0,0,0.05)', color: MUTED,
                      border: '1px solid rgba(0,0,0,0.12)', letterSpacing: '0.4px',
                      textTransform: 'uppercase' }}>{opt.hint}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 6,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <Users size={11} /> Guest filter
          </div>
          <div
            data-testid="oscar-conv-guest-filter-disabled"
            title="Guest-linked threads activate when token-aware Oscar mounting ships in Phase 2. Today all conversations are anonymous public-site visitors."
            style={{ padding: '10px 12px', borderRadius: 10,
              background: 'rgba(122, 106, 64, 0.06)',
              border: `1px dashed ${G_PANEL_BORDER}`,
              cursor: 'not-allowed', opacity: 0.85 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>Pending</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.6px',
                textTransform: 'uppercase', padding: '2px 6px', borderRadius: 99,
                background: 'rgba(198,167,90,0.18)', color: GOLD_DK,
                border: '1px solid rgba(198,167,90,0.30)' }}>Phase 2</span>
            </div>
            <div style={{ fontSize: 10, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
              Linked: <strong style={{ color: DARK }}>{linkedCount}</strong> / {threads.length}
            </div>
            <div style={{ fontSize: 10, color: MUTED, marginTop: 4, lineHeight: 1.45, fontStyle: 'italic' }}>
              Activates once chat carries the guest token.
            </div>
          </div>
        </div>

        {/* ── Center pane ───────────────────────────────────────────── */}
        <div style={{ background: G_PANEL_BG, backdropFilter: G_BLUR, WebkitBackdropFilter: G_BLUR,
          border: `1px solid ${G_PANEL_BORDER}`, borderRadius: 14,
          boxShadow: G_PANEL_SHADOW, overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', borderBottom: `1px solid ${G_PANEL_BORDER}` }}>
            <div>
              <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 20, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
                {viewMode === 'sessions' ? 'Sessions' : 'Recent Conversations'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: MUTED }}>
                {viewMode === 'sessions'
                  ? `${threads.length} thread${threads.length === 1 ? '' : 's'} · admin-only · read-only`
                  : `${filtered.length} of ${logs.length} turns · admin-only · read-only`}
              </p>
            </div>
            <button
              data-testid="oscar-logs-refresh"
              onClick={fetchLogs}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,252,246,0.7)', border: `1px solid ${G_PANEL_BORDER}`,
                color: GOLD_DK, padding: '6px 12px', borderRadius: 10,
                cursor: loading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600,
                letterSpacing: '0.5px', textTransform: 'uppercase' }}
            >
              <RefreshCw size={12} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
              Refresh
            </button>
          </div>

          {/* Body — Sessions view */}
          {viewMode === 'sessions' && (
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
              maxHeight: 620, overflowY: 'auto' }}>
              {error && (
                <div data-testid="oscar-logs-error" style={{ padding: 14, borderRadius: 10,
                  background: 'rgba(178,58,72,0.08)', color: FLAG_RED, fontSize: 12,
                  border: '1px solid rgba(178,58,72,0.20)' }}>
                  Failed to load logs: {error}
                </div>
              )}
              {!error && !loading && threads.length === 0 && (
                <div data-testid="oscar-logs-empty" style={{ padding: '40px 20px',
                  textAlign: 'center', color: MUTED, fontSize: 13, fontStyle: 'italic' }}>
                  No conversations recorded yet. Once guests start chatting with Oscar, their visits will appear here as threads.
                </div>
              )}
              {threads.map((t, idx) => {
                const active = t.sessionId === selectedThreadId;
                const sidShort = (t.sessionId || 'anon').slice(0, 12);
                const turnCount = t.turns.length;
                const labels = Object.keys(t.labelCounts);
                return (
                  <button
                    key={t.sessionId}
                    data-testid={`oscar-thread-${idx}`}
                    onClick={() => setSelectedThreadId(t.sessionId)}
                    style={{ position: 'relative', textAlign: 'left',
                      padding: '12px 14px 12px 16px', borderRadius: 12,
                      background: active ? '#FFFFFF' : 'rgba(248, 248, 250, 0.55)',
                      border: `1px solid ${active ? GOLD : G_PANEL_BORDER}`,
                      cursor: 'pointer',
                      boxShadow: active ? '0 6px 22px rgba(198,167,90,0.18)' : 'none',
                      transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                      animation: `fadeInRow 0.22s ease ${idx * 35}ms both` }}
                  >
                    {active && (
                      <span aria-hidden style={{ position: 'absolute', left: 0, top: 8, bottom: 8,
                        width: 2, background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_DK} 100%)`,
                        borderRadius: 2 }} />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginBottom: 6, gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: GOLD_DK,
                          letterSpacing: '0.6px', textTransform: 'uppercase',
                          fontFamily: 'monospace, monospace' }}>
                          {sidShort}
                        </span>
                        {!t.linked && (
                          <span style={{ fontSize: 9, fontWeight: 600, color: MUTED,
                            padding: '1px 6px', borderRadius: 99,
                            background: 'rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.10)' }}>anonymous</span>
                        )}
                        {/* Mode pill (Feb 2026 reset) */}
                        <span data-testid={`oscar-thread-${idx}-mode`}
                          title={`Mode: ${t.dominantMode}`}
                          style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.4px',
                            textTransform: 'uppercase', padding: '1px 6px', borderRadius: 99,
                            background: t.dominantMode === 'enhanced' ? 'rgba(30,115,68,0.10)' : 'rgba(198,167,90,0.10)',
                            color: t.dominantMode === 'enhanced' ? '#1E7344' : GOLD_DK,
                            border: `1px solid ${t.dominantMode === 'enhanced' ? 'rgba(30,115,68,0.28)' : G_PANEL_BORDER}` }}>
                          {t.dominantMode}
                        </span>
                        {t.flaggedCount > 0 && (
                          <span title={`${t.flaggedCount} flagged turn(s)`} style={{
                            width: 7, height: 7, borderRadius: '50%', background: FLAG_RED,
                            display: 'inline-block' }} />
                        )}
                      </div>
                      <span style={{ fontSize: 10, color: MUTED }}>{fmtRelative(t.lastAt)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: DARK, lineHeight: 1.5,
                      fontWeight: 500, marginBottom: 6 }}>
                      {truncate(t.turns[0]?.user_msg, 110) || '—'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {labels.slice(0, 3).map(lbl => (
                          <span key={lbl} style={{ fontSize: 9, fontWeight: 600,
                            letterSpacing: '0.4px', textTransform: 'uppercase',
                            padding: '2px 7px', borderRadius: 99,
                            background: 'rgba(198,167,90,0.10)', color: GOLD_DK,
                            border: `1px solid ${G_PANEL_BORDER}` }}>{lbl}</span>
                        ))}
                      </div>
                      <span style={{ fontSize: 10, color: MUTED }}>
                        {turnCount} turn{turnCount === 1 ? '' : 's'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Body — Feed view (preserves prior flat behavior) */}
          {viewMode === 'feed' && (
            <div style={{ padding: '6px 14px 14px' }}>
              {error && (
                <div data-testid="oscar-logs-error" style={{ padding: 14, borderRadius: 10,
                  background: 'rgba(178,58,72,0.08)', color: FLAG_RED, fontSize: 12,
                  border: '1px solid rgba(178,58,72,0.20)', margin: '8px 0' }}>
                  Failed to load logs: {error}
                </div>
              )}
              {!error && !loading && filtered.length === 0 && (
                <div data-testid="oscar-logs-empty" style={{ padding: '40px 20px',
                  textAlign: 'center', color: MUTED, fontSize: 13, fontStyle: 'italic' }}>
                  No conversations match your search.
                </div>
              )}
              {!error && filtered.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 110px 22px',
                  gap: 10, padding: '8px 10px', fontSize: 9, fontWeight: 700,
                  color: MUTED, textTransform: 'uppercase', letterSpacing: '0.6px',
                  borderBottom: `1px solid ${G_PANEL_BORDER}` }}>
                  <div>When</div>
                  <div>Question</div>
                  <div>Label</div>
                  <div></div>
                </div>
              )}
              {filtered.map((log, idx) => {
                const rowId = log.session_id ? `${log.session_id}-${idx}` : `row-${idx}`;
                const expanded = expandedFeedId === rowId;
                const flagged = !!log.flagged_reason;
                return (
                  <div key={rowId} data-testid={`oscar-log-row-${idx}`}
                    style={{ borderBottom: `1px solid ${G_PANEL_BORDER}`,
                      background: expanded ? 'rgba(0,0,0,0.025)' : 'transparent' }}>
                    <div onClick={() => setExpandedFeedId(expanded ? null : rowId)}
                      style={{ display: 'grid', gridTemplateColumns: '90px 1fr 110px 22px',
                        gap: 10, padding: '12px 10px', cursor: 'pointer',
                        fontSize: 12, color: DARK, alignItems: 'flex-start' }}>
                      <div title={log.created_at} style={{ color: MUTED, fontSize: 11, paddingTop: 2 }}>
                        {fmtRelative(log.created_at)}
                      </div>
                      <div style={{ fontWeight: 500 }}>
                        {truncate(log.user_msg, 180)}
                        {flagged && (
                          <span title={log.flagged_reason} style={{ marginLeft: 6,
                            width: 7, height: 7, borderRadius: '50%',
                            background: FLAG_RED, display: 'inline-block', verticalAlign: 'middle' }} />
                        )}
                      </div>
                      <div>
                        <span style={{ fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.5px', textTransform: 'uppercase',
                          padding: '2px 7px', borderRadius: 99,
                          background: 'rgba(198,167,90,0.10)', color: GOLD_DK,
                          border: `1px solid ${G_PANEL_BORDER}` }}>
                          {displayClassifierLabel(log.classifier_label)}
                        </span>
                        <div style={{ marginTop: 4 }}>
                          <span data-testid={`oscar-log-row-${idx}-mode`}
                            style={{ fontSize: 9, fontWeight: 700,
                              letterSpacing: '0.4px', textTransform: 'uppercase',
                              padding: '1px 6px', borderRadius: 99,
                              background: (log.mode || 'basic') === 'enhanced' ? 'rgba(30,115,68,0.10)' : 'rgba(0,0,0,0.025)',
                              color: (log.mode || 'basic') === 'enhanced' ? '#1E7344' : MUTED,
                              border: `1px solid ${(log.mode || 'basic') === 'enhanced' ? 'rgba(30,115,68,0.28)' : 'rgba(0,0,0,0.12)'}` }}>
                            {log.mode || 'basic'}
                          </span>
                        </div>
                      </div>
                      <div style={{ color: MUTED, paddingTop: 2 }}>
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>
                    {expanded && (
                      <div data-testid={`oscar-log-row-${idx}-expanded`}
                        style={{ padding: '4px 10px 16px', display: 'grid', gap: 10,
                          fontSize: 12, color: DARK }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: MUTED,
                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Reply</div>
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{log.assistant_msg || '—'}</div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16,
                          fontSize: 11, color: MUTED, paddingTop: 6,
                          borderTop: `1px solid ${G_PANEL_BORDER}` }}>
                          <div><strong style={{ color: DARK }}>Source:</strong> {log.source || '—'}</div>
                          <div><strong style={{ color: DARK }}>Session:</strong> {(log.session_id || '—').slice(0, 12)}</div>
                          <div><strong style={{ color: DARK }}>UTC:</strong> {log.created_at || '—'}</div>
                          {log.flagged_reason && (
                            <div style={{ color: FLAG_RED }}>
                              <strong>Flag:</strong> {log.flagged_reason}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right pane — selected thread detail (Sessions only) ──── */}
        {viewMode === 'sessions' && (
          <div data-testid="oscar-thread-detail"
            style={{ background: G_PANEL_BG, backdropFilter: G_BLUR, WebkitBackdropFilter: G_BLUR,
              border: `1px solid ${G_PANEL_BORDER}`, borderRadius: 14,
              boxShadow: G_PANEL_SHADOW, overflow: 'hidden',
              minHeight: 320, position: 'sticky', top: 0 }}>

            {!selectedThread && (
              <div style={{ padding: '64px 28px', textAlign: 'center', color: MUTED,
                fontSize: 13, fontStyle: 'italic' }}>
                Select a thread to read the full conversation.
              </div>
            )}

            {selectedThread && (
              <>
                <div style={{ padding: '14px 18px', borderBottom: `1px solid ${G_PANEL_BORDER}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.7px',
                      textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>
                      Visit · <span style={{ fontFamily: 'monospace, monospace', color: GOLD_DK }}>
                        {selectedThread.sessionId.slice(0, 16)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: MUTED }}>
                      {selectedThread.turns.length} turn{selectedThread.turns.length === 1 ? '' : 's'}
                      {' · '}{fmtRelative(selectedThread.firstAt)} → {fmtRelative(selectedThread.lastAt)}
                    </div>
                  </div>
                  {!selectedThread.linked && (
                    <span style={{ fontSize: 9, fontWeight: 600, color: MUTED,
                      padding: '3px 8px', borderRadius: 99,
                      background: 'rgba(0,0,0,0.04)',
                      border: '1px solid rgba(0,0,0,0.10)',
                      letterSpacing: '0.4px', textTransform: 'uppercase' }}>Anonymous</span>
                  )}
                </div>
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column',
                  gap: 14, maxHeight: 580, overflowY: 'auto' }}>
                  {selectedThread.turns.map((turn, ti) => (
                    <div key={ti} data-testid={`oscar-thread-turn-${ti}`}
                      style={{ animation: `fadeInRow 0.24s ease ${ti * 30}ms both` }}>
                      {/* Question bubble */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                        <div style={{ maxWidth: '88%',
                          padding: '10px 14px', borderRadius: '16px 16px 4px 16px',
                          background: 'rgba(198, 167, 90, 0.14)',
                          border: '1px solid rgba(198, 167, 90, 0.28)',
                          fontSize: 13, lineHeight: 1.55, color: DARK,
                          whiteSpace: 'pre-wrap' }}>
                          {turn.user_msg || '—'}
                        </div>
                      </div>
                      {/* Reply bubble */}
                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{ maxWidth: '88%',
                          padding: '10px 14px', borderRadius: '16px 16px 16px 4px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: `1px solid ${G_PANEL_BORDER}`,
                          fontSize: 13, lineHeight: 1.55, color: '#2A241B',
                          whiteSpace: 'pre-wrap' }}>
                          {turn.assistant_msg || '—'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 6,
                        fontSize: 10, color: MUTED, letterSpacing: '0.3px' }}>
                        <span title={turn.created_at}>{fmtRelative(turn.created_at)}</span>
                        <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px',
                          fontWeight: 600, color: GOLD_DK }}>
                          {displayClassifierLabel(turn.classifier_label)}
                        </span>
                        {turn.flagged_reason && (
                          <span style={{ color: FLAG_RED }}>flagged: {turn.flagged_reason}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeInRow { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 1100px) {
          [data-testid="oscar-subtab-panel-conversations"] > div:nth-child(2) {
            grid-template-columns: 1fr !important;
          }
          [data-testid="oscar-conv-left-rail"] { position: static !important; }
        }
        @supports not (backdrop-filter: blur(1px)) {
          [data-testid="oscar-subtab-panel-conversations"] [style*="backdropFilter"] {
            background: ${SURFACE} !important;
          }
        }
      `}</style>
    </div>
  );
};

// ─── Oscar → Settings panel (Feb 2026 reset) ────────────────────────────────
// Oscar Mode segmented control. Basic active. Enhanced visibly disabled with
// "Coming Soon" until the v2 brain ships. Retention block underneath shows
// the read-only event-date+14d window with placeholder Keep/Archive/Delete
// buttons (all disabled — no destructive logic in this pass).
const OscarSettingsPanel = ({ oscarMode }) => {
  return (
    <div data-testid="oscar-subtab-panel-settings"
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '24px 26px', display: 'flex',
        flexDirection: 'column', gap: 22 }}>

      {/* Oscar Mode — descriptors only. Master control lives at the top of the
          Oscar area to avoid being mistaken for a buried setting. */}
      <section data-testid="oscar-mode-section">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
            Oscar Mode
          </h3>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
            textTransform: 'uppercase', color: GOLD_DK }}>
            Operating model
          </span>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: MUTED, lineHeight: 1.6, maxWidth: 620 }}>
          The single safety brake for guest-facing Oscar. Switching is immediate;
          the next chat call uses the new mode. Currently only <strong>Basic</strong> is live.
        </p>

        {/* Pointer to the master control */}
        <div data-testid="oscar-mode-managed-at-top"
          style={{ display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 10,
            border: `1px solid ${BORDER_GOLD}`,
            background: 'rgba(198,167,90,0.06)', maxWidth: 720 }}>
          <Lock size={14} color={GOLD_DK} />
          <div style={{ fontSize: 12, color: DARK, lineHeight: 1.55 }}>
            <strong>Master control lives at the top of the Oscar area.</strong>
            <span style={{ color: MUTED, marginLeft: 6 }}>
              Scroll up to switch live mode. Currently <strong style={{ color: GOLD_DK }}>{oscarMode === 'basic' ? 'Basic' : 'Enhanced'}</strong>.
            </span>
          </div>
        </div>

        {/* Mode descriptors */}
        <div style={{ marginTop: 16, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 720 }}>
          <div style={{ padding: 14, borderRadius: 10,
            background: oscarMode === 'basic' ? 'rgba(0,0,0,0.025)' : 'transparent',
            border: `1px solid ${oscarMode === 'basic' ? BORDER_GOLD : BORDER}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', color: GOLD_DK, marginBottom: 6 }}>Basic — live</div>
            <p style={{ margin: 0, fontSize: 12, color: DARK, lineHeight: 1.6 }}>
              Today's safe baseline. Simpler chat path with the existing system prompt. No relevance gate, no curated KB injection, no tool calls. Predictable. Logs tagged <code style={{ fontSize: 11 }}>mode: "basic"</code>.
            </p>
          </div>
          <div style={{ padding: 14, borderRadius: 10,
            background: 'transparent',
            border: `1px dashed ${BORDER}`, opacity: 0.85 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>Enhanced — Coming Soon</div>
            <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.6, fontStyle: 'italic' }}>
              Future v2 architecture: relevance classifier, curated knowledge-base injection, soft-budget tracking, luxury concierge persona. Unlocks in a later phase once each piece is built and approved.
            </p>
          </div>
        </div>
      </section>

      {/* Retention */}
      <section data-testid="oscar-retention-section"
        style={{ paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
        <h3 style={{ margin: '0 0 6px', fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
          Retention
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: MUTED, lineHeight: 1.6, maxWidth: 620 }}>
          Conversation logs live in MongoDB on the same instance as guest data
          (collection: <code>oscar_logs</code>). Default retention window: <strong>event date + 14 days</strong>.
          After the window elapses, an in-app reminder will surface here.
          No logs are deleted automatically.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { id: 'keep',    label: 'Keep for now' },
            { id: 'archive', label: 'Archive / export' },
            { id: 'delete',  label: 'Delete logs for this event' },
          ].map(b => (
            <button
              key={b.id}
              disabled
              data-testid={`oscar-retention-${b.id}`}
              title="Coming Soon — destructive actions land in a later phase with double confirmation + audit trail."
              style={{ padding: '8px 14px', borderRadius: 10, border: `1px dashed ${BORDER}`,
                background: 'rgba(0,0,0,0.02)', color: MUTED,
                fontSize: 12, fontWeight: 600, letterSpacing: '0.4px',
                textTransform: 'uppercase', cursor: 'not-allowed', opacity: 0.7 }}
            >
              {b.label}
              <span style={{ marginLeft: 8, fontSize: 9, padding: '2px 6px',
                borderRadius: 99, background: 'rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.12)' }}>Coming Soon</span>
            </button>
          ))}
        </div>
      </section>

      {/* Other deferred controls */}
      <section data-testid="oscar-other-controls-section"
        style={{ paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
        <h3 style={{ margin: '0 0 6px', fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
          Future controls
        </h3>
        <p style={{ margin: '0 0 6px', fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
          These knobs are reserved for the Enhanced phase: persona alignment, abuse heuristics, internet-category permissions, soft-budget tracking. Not interactive yet.
        </p>
      </section>
    </div>
  );
};

// ─── Oscar → Knowledge Base subtab — Phase 2C admin CRUD ───────────────────
// Admin-only surface. Reads/writes /api/kb/facts. Nothing here is wired to
// guest chat yet — that arrives in Phase 2D. Until then this panel is a
// pure curation tool: create / edit / publish / unpublish / delete facts.
const KB_PANEL_BG     = '#FFFFFF';
const KB_PANEL_BORDER = 'rgba(0,0,0,0.10)';
const KB_INPUT_BG     = '#FAFAFB';
const KB_TOPIC_FALLBACK = [
  'general', 'hotels', 'flights', 'attire', 'dietary', 'accessibility',
  'gifts', 'schedule', 'venue', 'transport', 'local_guide', 'kids',
  'rsvp', 'other',
];

const KbStatusBadge = ({ status }) => {
  const live = status === 'published';
  const palette = live
    ? { bg: 'rgba(30,115,68,0.10)', fg: '#1E7344', br: 'rgba(30,115,68,0.30)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}` }}>
      {live ? 'Published' : 'Draft'}
    </span>
  );
};

// ─── Track A: deterministic local phrase extractor ─────────────────────────
// Runs in <50ms on the user's textarea. Returns one entry per non-empty line
// with 2-4 candidate phrases of interest. Drives the visible "lifting" in
// Beats 1-2 of the cinematic reveal — every chip the user sees is a literal
// substring of what they typed. No fakes.
const KB_TRACK_A_TIME_RE   = /\b(\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?))\b|\b(?:noon|midnight)\b/gi;
const KB_TRACK_A_MONEY_RE  = /\$\s*\d+(?:\.\d{1,2})?\b/g;
const KB_TRACK_A_DATE_RE   = /\b(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s*\d{4})?\b/gi;
const KB_TRACK_A_NAMED_RE  = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3})\b/g;
const KB_TRACK_A_ROLE_RE   = /\b(?:the\s+)?(?:host|hostess|best\s+man|maid\s+of\s+honor|matron\s+of\s+honor|bridesmaid|groomsman|usher|officiant|emcee|dj|host\s+hotel|host\s+venue)\b/gi;
const KB_TRACK_A_DESCR_RE  = /\b(?:is|are|will\s+be)\s+(formal|semi[\s-]?formal|black[\s-]?tie|cocktail|casual|business|available|required|optional|provided|welcome|encouraged|complimentary)\b/gi;

const kbTrackAExtract = (text) => {
  const out = [];
  if (!text) return out;
  const lines = text.split('\n');
  let charOffset = 0;
  for (const rawLine of lines) {
    const line = rawLine;
    if (!line.trim()) {
      charOffset += line.length + 1;
      continue;
    }
    const phrases = [];
    const seenSpans = []; // [start,end] in line coords; prevents overlap
    const overlaps = (s, e) => seenSpans.some(([ss, ee]) => !(e <= ss || s >= ee));
    const collect = (re, kind) => {
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(line)) !== null) {
        const matched = m[0];
        const s = m.index;
        const e = s + matched.length;
        if (overlaps(s, e)) continue;
        seenSpans.push([s, e]);
        phrases.push({ text: matched, kind, start: s, end: e });
        if (phrases.length >= 5) break;
      }
    };
    collect(KB_TRACK_A_DATE_RE,  'date');
    collect(KB_TRACK_A_TIME_RE,  'time');
    collect(KB_TRACK_A_MONEY_RE, 'money');
    collect(KB_TRACK_A_ROLE_RE,  'role');
    collect(KB_TRACK_A_NAMED_RE, 'entity');
    collect(KB_TRACK_A_DESCR_RE, 'descriptor');
    // Sort phrases by their position in the line for stable left→right reveal.
    phrases.sort((a, b) => a.start - b.start);
    out.push({ line, phrases: phrases.slice(0, 4), charOffset });
    charOffset += line.length + 1;
  }
  return out.filter(o => o.line.trim());
};

// ─── KbAgentFigure — chunky chrome agents inspired by the reference ────────
// Premium machine-intelligence aesthetic: dome head + cyan visor eyes + gold
// chest emblem + chrome plating + boxy feet. Three variants (Reader, Curator,
// Structurer) share the silhouette so they read as a coordinated team — but
// each carries a distinct chest insignia describing the work they do.
const KbAgentFigure = ({ kind, size = 156, carrying = false }) => {
  // Common color tokens for chrome / cyan / gold.
  const chrome     = 'url(#kbChromeGrad)';
  const chromeDark = 'url(#kbChromeDark)';
  const visorBlack = 'url(#kbVisor)';
  const eyeCyan    = '#7ED9F2';
  const eyeCore    = '#E2FAFF';
  const goldFill   = '#C6A75A';
  const goldDk     = '#9D7E3E';
  // Chest insignia varies per agent.
  const insignia = kind === 'reader'
    ? <text x="50" y="91" textAnchor="middle" fontFamily="monospace" fontSize="6.5"
        fill={goldFill} letterSpacing="0.6" fontWeight="700">READ</text>
    : kind === 'curator'
    ? <text x="50" y="91" textAnchor="middle" fontFamily="monospace" fontSize="6.5"
        fill={goldFill} letterSpacing="0.6" fontWeight="700">SORT</text>
    : <text x="50" y="91" textAnchor="middle" fontFamily="monospace" fontSize="6.5"
        fill={goldFill} letterSpacing="0.6" fontWeight="700">BUILD</text>;

  return (
    <svg width={size} height={size * 1.15} viewBox="0 0 100 115"
      aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        {/* Body chrome gradient — cool metallic */}
        <linearGradient id="kbChromeGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#F4F5F7" />
          <stop offset="45%" stopColor="#D8DBE0" />
          <stop offset="80%" stopColor="#9098A2" />
          <stop offset="100%" stopColor="#6E7681" />
        </linearGradient>
        <linearGradient id="kbChromeDark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#3A3F47" />
          <stop offset="60%" stopColor="#1F2329" />
          <stop offset="100%" stopColor="#0F1115" />
        </linearGradient>
        {/* Visor — deep glossy black */}
        <linearGradient id="kbVisor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#2A2E36" />
          <stop offset="50%" stopColor="#0A0C10" />
          <stop offset="100%" stopColor="#1A1D24" />
        </linearGradient>
        {/* Cyan eye glow */}
        <radialGradient id="kbEyeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor={eyeCore} stopOpacity="1" />
          <stop offset="55%" stopColor={eyeCyan} stopOpacity="0.95" />
          <stop offset="100%" stopColor={eyeCyan} stopOpacity="0" />
        </radialGradient>
        {/* Floor reflection */}
        <linearGradient id="kbReflect" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#000000" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* ─── Floor reflection (under feet) ─── */}
      <ellipse cx="50" cy="111" rx="28" ry="3" fill="url(#kbReflect)" />

      {/* ─── Body torso ─── */}
      <path d="M28 64 Q28 60 32 58 L68 58 Q72 60 72 64 L74 96 Q74 102 68 104 L32 104 Q26 102 26 96 Z"
        fill={chrome} stroke="#5A6068" strokeWidth="0.5" />
      {/* Chest plate (dark inset for insignia) */}
      <path d="M37 70 Q37 68 39 68 L61 68 Q63 68 63 70 L63 94 Q63 96 61 96 L39 96 Q37 96 37 94 Z"
        fill={chromeDark} />
      <rect x="38.5" y="82" width="23" height="13" fill="rgba(0,0,0,0.55)" rx="1.5" />
      {insignia}
      {/* Gold accent neckline */}
      <path d="M38 68 L62 68" stroke={goldFill} strokeWidth="0.8" />
      <path d="M44 70 L50 76 L56 70" stroke={goldFill} strokeWidth="0.8" fill="none" />
      <circle cx="50" cy="78" r="0.9" fill={goldFill} />

      {/* ─── Shoulders & arms ─── */}
      {/* Left shoulder pauldron */}
      <ellipse cx="26" cy="63" rx="6" ry="6" fill={chrome} stroke="#5A6068" strokeWidth="0.4" />
      <ellipse cx="26" cy="63" rx="3" ry="3.2" fill={chromeDark} />
      {/* Right shoulder pauldron */}
      <ellipse cx="74" cy="63" rx="6" ry="6" fill={chrome} stroke="#5A6068" strokeWidth="0.4" />
      <ellipse cx="74" cy="63" rx="3" ry="3.2" fill={chromeDark} />
      {/* Left arm */}
      <path d="M22 67 Q19 73 19 80 Q19 86 22 89 L26 89 Q28 87 28 84 L28 72 Q26 68 22 67 Z"
        fill={chrome} stroke="#5A6068" strokeWidth="0.4" />
      <ellipse cx="22" cy="89" rx="4" ry="3.5" fill={chromeDark} />
      {/* Right arm */}
      <path d="M78 67 Q81 73 81 80 Q81 86 78 89 L74 89 Q72 87 72 84 L72 72 Q74 68 78 67 Z"
        fill={chrome} stroke="#5A6068" strokeWidth="0.4" />
      <ellipse cx="78" cy="89" rx="4" ry="3.5" fill={chromeDark} />

      {/* ─── Feet ─── */}
      <path d="M34 102 L34 109 Q34 110 35 110 L46 110 Q47 110 47 109 L47 102 Z"
        fill={chromeDark} stroke="#5A6068" strokeWidth="0.3" />
      <path d="M53 102 L53 109 Q53 110 54 110 L65 110 Q66 110 66 109 L66 102 Z"
        fill={chromeDark} stroke="#5A6068" strokeWidth="0.3" />
      <ellipse cx="40.5" cy="109" rx="6" ry="1.5" fill="#0A0C10" />
      <ellipse cx="59.5" cy="109" rx="6" ry="1.5" fill="#0A0C10" />

      {/* ─── Head ─── */}
      {/* Dome cap (glossy chrome top half) */}
      <path d="M22 30 Q22 14 50 14 Q78 14 78 30 L78 38 L22 38 Z"
        fill={chrome} stroke="#4A5058" strokeWidth="0.5" />
      {/* Specular highlight on the cap */}
      <path d="M30 18 Q40 14 52 16 Q44 18 36 22 Q32 24 30 28 Z"
        fill="rgba(255,255,255,0.55)" />
      {/* Lower head/chin (darker shell) */}
      <path d="M22 38 L22 50 Q22 56 28 58 L72 58 Q78 56 78 50 L78 38 Z"
        fill={chromeDark} />
      {/* Side ear-housings */}
      <ellipse cx="20" cy="44" rx="3.5" ry="6" fill={chromeDark} stroke="#3A3F47" strokeWidth="0.4" />
      <ellipse cx="80" cy="44" rx="3.5" ry="6" fill={chromeDark} stroke="#3A3F47" strokeWidth="0.4" />
      {/* Visor (the glossy black band) */}
      <path d="M27 32 Q27 28 32 28 L68 28 Q73 28 73 32 L73 48 Q73 52 68 52 L32 52 Q27 52 27 48 Z"
        fill={visorBlack} stroke="#2A2E36" strokeWidth="0.4" />
      {/* Visor reflection sheen */}
      <path d="M30 30 Q44 28 60 30 Q56 32 50 33 Q42 34 32 36 Z"
        fill="rgba(255,255,255,0.10)" />
      {/* Cyan eyes */}
      <ellipse cx="40" cy="40" rx="6" ry="4.2" fill="url(#kbEyeGlow)" />
      <ellipse cx="60" cy="40" rx="6" ry="4.2" fill="url(#kbEyeGlow)" />
      <ellipse cx="40" cy="40" rx="3.2" ry="2.4" fill={eyeCore} opacity="0.95" />
      <ellipse cx="60" cy="40" rx="3.2" ry="2.4" fill={eyeCore} opacity="0.95" />
      {/* Antenna with gold star */}
      <line x1="50" y1="14" x2="50" y2="6" stroke="#5A6068" strokeWidth="0.8" />
      <g transform="translate(50 4)">
        <path d="M0 -3 L1 -1 L3 0 L1 1 L0 3 L-1 1 L-3 0 L-1 -1 Z" fill={goldFill} />
        <path d="M0 -2 L0.6 -0.5 L2 0 L0.6 0.5 L0 2 L-0.6 0.5 L-2 0 L-0.6 -0.5 Z" fill={goldDk} />
      </g>

      {/* ─── Carry indicator: glow at front-of-chest when carrying ─── */}
      {carrying && (
        <g>
          <ellipse cx="50" cy="86" rx="11" ry="3.5" fill={goldFill} opacity="0.55" />
          <ellipse cx="50" cy="86" rx="6" ry="2" fill="#FFE9A8" opacity="0.85" />
        </g>
      )}
    </svg>
  );
};

// ─── KbComposer — Screen 1: simple, human-first fact entry ────────────────
const KbComposer = ({ initialText, onCancel, onContinue }) => {
  const [text, setText] = useState(initialText || '');
  const taRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => taRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const lines = text.split('\n').filter(l => l.trim()).length;
  const canContinue = lines > 0 && !!text.trim();

  return (
    <div data-testid="oscar-subtab-panel-kb"
      style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
        background: KB_PANEL_BG, padding: '28px 32px', display: 'flex',
        flexDirection: 'column', gap: 18, minHeight: 480 }}>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: GOLD_DK, marginBottom: 6 }}>
            Teach Oscar
          </div>
          <h3 style={{ margin: 0, fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 26, fontWeight: 700, color: DARK, letterSpacing: '-0.2px' }}>
            What should Oscar know?
          </h3>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: MUTED, lineHeight: 1.6, maxWidth: 580 }}>
            Type one fact per line. Plain English. Oscar will quote these to guests once you publish.
          </p>
        </div>
        <button data-testid="kb-composer-cancel" onClick={onCancel}
          style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
            background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
          Cancel
        </button>
      </div>

      <div style={{ position: 'relative' }}>
        <textarea data-testid="kb-composer-textarea" ref={taRef}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={10}
          placeholder={"e.g.\nThe party starts at 12 p.m.\nThe attire is formal.\nValet parking is available.\nThe host hotel is the Saddle Brook Marriott."}
          style={{ width: '100%', padding: '16px 18px',
            borderRadius: 12, border: `1px solid ${KB_PANEL_BORDER}`,
            background: KB_INPUT_BG, fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 18, color: DARK, lineHeight: 1.7, outline: 'none',
            resize: 'vertical', minHeight: 220 }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>
          {lines === 0 ? '\u00A0' : `${lines} ${lines === 1 ? 'fact' : 'facts'} ready`}
        </span>
        <button data-testid="kb-composer-continue" onClick={() => onContinue(text)}
          disabled={!canContinue}
          style={{ padding: '12px 24px', borderRadius: 12, border: 'none',
            background: canContinue
              ? `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`
              : 'rgba(0,0,0,0.06)',
            color: canContinue ? '#1B1812' : MUTED,
            fontSize: 12, fontWeight: 800, letterSpacing: '0.7px',
            textTransform: 'uppercase', cursor: canContinue ? 'pointer' : 'not-allowed',
            boxShadow: canContinue ? '0 3px 12px rgba(198,167,90,0.30)' : 'none',
            transition: 'all 0.18s ease' }}>
          Continue ▸
        </button>
      </div>
    </div>
  );
};

// ─── KbReveal — Screen 2: cinematic AI-agent stage ────────────────────────
// One single mode. ~13s typical. Skippable (Esc / click / Enter / Space).
// Reduced-motion: collapses to a 250ms fade.
//
// Stage anatomy:
//   • A premium cool-gray cinematic workspace (NOT black) with subtle floor.
//   • The user's typed lines are rendered large in serif on the upper page.
//   • Three large chrome-and-cyan agents (Reader, Curator, Structurer) are
//     foreground actors — they walk on stage, reach for chips containing
//     the user's literal phrases, and physically carry them to destinations.
//   • An "Oscar" recipient figure stands at stage-right; finished cards are
//     ribboned to it, visibly routed.
const REVEAL_BEAT_DURATIONS = {
  reader:     3000,
  curator:    4400,
  structurer: 5200,
  resolve:    1000,
};
const KB_TOPIC_LABEL = {
  hotels:        'Hotels',
  attire:        'Attire',
  dietary:       'Food & Dietary',
  flights:       'Flights',
  transport:     'Travel & Parking',
  venue:         'Venue',
  schedule:      'Schedule',
  gifts:         'Gifts',
  rsvp:          'RSVP',
  kids:          'Kids',
  accessibility: 'Accessibility',
  local_guide:   'Local Guide',
  general:       'General',
  other:         'Other',
};

const KbReveal = ({ rawText, trackA, structuringPromise, onComplete, onCancel }) => {
  const [beat, setBeat] = useState(0);             // 0..4 (0 = mounting, 4 = done)
  const [structuring, setStructuring] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [currentChip, setCurrentChip] = useState(-1);   // sub-beat for Curator
  const [currentCard, setCurrentCard] = useState(-1);   // sub-beat for Structurer
  const completedRef = useRef(false);

  // Detect reduced-motion preference.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(m.matches);
    const fn = (e) => setReducedMotion(e.matches);
    m.addEventListener?.('change', fn);
    return () => m.removeEventListener?.('change', fn);
  }, []);

  // Track B — start the structuring API call immediately on mount.
  useEffect(() => {
    let cancelled = false;
    structuringPromise.then(d => {
      if (!cancelled) setStructuring(d);
    }).catch(() => {
      if (cancelled) return;
      const fallback = trackA.map(t => ({
        raw_text: t.line, topic: 'general', topic_label: 'General',
        tags: [], question_patterns: [t.line.replace(/\.$/, '') + '?'],
        canonical_answer: t.line, confidence: 0.4,
        ambiguity_reason: 'Auto-generated fallback — please review.',
        person_subject: null,
      }));
      setStructuring({ facts: fallback });
    });
    return () => { cancelled = true; };
  }, [structuringPromise, trackA]);

  const skipNow = useCallback(() => {
    if (completedRef.current) return;
    setSkipped(true);
  }, []);

  // Beat advancement.
  useEffect(() => {
    if (reducedMotion) {
      const t = setTimeout(() => skipNow(), 250);
      return () => clearTimeout(t);
    }
    if (skipped) return;
    if (beat === 0) { setBeat(1); return; }
    if (beat >= 4) return;
    const dur = beat === 1 ? REVEAL_BEAT_DURATIONS.reader
              : beat === 2 ? REVEAL_BEAT_DURATIONS.curator
              : beat === 3 ? REVEAL_BEAT_DURATIONS.structurer
              :              REVEAL_BEAT_DURATIONS.resolve;
    const t = setTimeout(() => setBeat(beat + 1), dur);
    return () => clearTimeout(t);
  }, [beat, reducedMotion, skipped, skipNow]);

  // Curator sub-beat: cycle through chips while carrying. Total chips known
  // from trackA. Each chip cycle is ~900ms (grab, carry, drop).
  const totalChips = trackA.reduce((acc, t) => acc + t.phrases.length, 0);
  useEffect(() => {
    if (beat !== 2 || skipped || reducedMotion) return;
    if (totalChips === 0) return;
    setCurrentChip(0);
    const intervals = [];
    for (let i = 1; i < totalChips; i++) {
      intervals.push(setTimeout(() => setCurrentChip(i), 600 + i * 800));
    }
    return () => intervals.forEach(clearTimeout);
  }, [beat, skipped, reducedMotion, totalChips]);

  // Structurer sub-beat: cycle through cards being assembled.
  useEffect(() => {
    if (beat !== 3 || skipped || reducedMotion) return;
    const n = trackA.length;
    if (n === 0) return;
    setCurrentCard(0);
    const intervals = [];
    for (let i = 1; i < n; i++) {
      intervals.push(setTimeout(() => setCurrentCard(i), 800 + i * 1100));
    }
    return () => intervals.forEach(clearTimeout);
  }, [beat, skipped, reducedMotion, trackA.length]);

  // Resolve once both choreography and structuring are ready.
  useEffect(() => {
    if (completedRef.current) return;
    const choreoDone = reducedMotion || skipped || beat >= 4;
    if (choreoDone && structuring) {
      completedRef.current = true;
      onComplete(structuring);
    }
  }, [beat, structuring, skipped, reducedMotion, onComplete]);

  // Hard 18s ceiling.
  useEffect(() => {
    const ceiling = setTimeout(() => {
      if (!completedRef.current && !structuring) {
        completedRef.current = true;
        onComplete({ facts: trackA.map(t => ({
          raw_text: t.line, topic: 'general', topic_label: 'General',
          tags: [], question_patterns: [t.line.replace(/\.$/, '') + '?'],
          canonical_answer: t.line, confidence: 0.4,
          ambiguity_reason: 'Structuring timed out — please review.',
          person_subject: null,
        })) });
      }
    }, 18000);
    return () => clearTimeout(ceiling);
  }, [structuring, trackA, onComplete]);

  // Keyboard skip.
  useEffect(() => {
    const onKey = (e) => {
      if (['Escape', 'Enter', ' ', 'Space'].includes(e.key)) {
        e.preventDefault();
        skipNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skipNow]);

  // Reduced-motion path.
  if (reducedMotion) {
    return (
      <div data-testid="kb-reveal-reduced-motion"
        style={{ padding: 40, textAlign: 'center', color: MUTED,
          background: KB_PANEL_BG, border: `1px solid ${KB_PANEL_BORDER}`,
          borderRadius: 14, opacity: 0.85, transition: 'opacity 250ms linear' }}>
        Organizing knowledge…
      </div>
    );
  }

  // Build flat chip list with origin + destination data.
  const facts = structuring?.facts || [];
  const allChips = [];
  trackA.forEach((entry, lineIdx) => {
    entry.phrases.forEach((p, phraseIdx) => {
      allChips.push({
        key: `${lineIdx}-${phraseIdx}`,
        text: p.text,
        kind: p.kind,
        lineIdx,
        phraseIdx,
        // The chip's destination is the plinth at `lineIdx` (one plinth per fact line).
        destBin: lineIdx,
      });
    });
  });

  // Per-fact bin metadata (label comes from Track B once it lands).
  const bins = trackA.map((t, i) => {
    const f = facts[i];
    return {
      key: `bin-${i}`,
      lineIdx: i,
      label: f ? (KB_TOPIC_LABEL[f.topic] || f.topic_label || 'General') : null,
      topic: f?.topic || null,
    };
  });

  const stageHeight = 700;
  // Agent positions per beat (translate-x of stage container).
  const readerLeft     = beat === 1 ? 'translateX(0)' : 'translateX(-160%)';
  const curatorLeft    = beat === 2 ? 'translateX(0)' : (beat > 2 ? 'translateX(-160%)' : 'translateX(0)');
  const curatorOpacity = beat === 2 ? 1 : 0;
  const readerOpacity  = beat === 1 ? 1 : 0;
  const structurerOp   = beat === 3 ? 1 : 0;
  const structurerLeft = beat === 3 ? 'translateX(0)' : 'translateX(160%)';

  return (
    <div data-testid="kb-reveal" onClick={skipNow}
      role="dialog" aria-label="AI agents organizing your facts. Press Esc to skip."
      style={{
        position: 'relative', width: '100%', minHeight: stageHeight,
        background: 'radial-gradient(circle at 50% 30%, #2A2D34 0%, #1A1B1F 60%, #131418 100%)',
        borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
        boxShadow: '0 14px 50px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.06)',
        userSelect: 'none',
      }}>

      {/* ── Stage ambient lighting (gold floor glow + soft top glow) ──── */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background:
          'radial-gradient(ellipse 70% 30% at 50% 100%, rgba(198,167,90,0.18) 0%, transparent 70%),' +
          'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(126,217,242,0.06) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* ── Stage floor line ──────────────────────────────────────────── */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, bottom: 110, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(198,167,90,0.45), transparent)',
        pointerEvents: 'none',
      }} />

      {/* ── Skip hint ─────────────────────────────────────────────────── */}
      <div aria-hidden style={{
        position: 'absolute', bottom: 14, right: 22,
        fontSize: 9, fontFamily: 'monospace', letterSpacing: '1.5px',
        color: 'rgba(244,241,232,0.45)', textTransform: 'uppercase',
      }}>
        press esc to skip
      </div>

      {/* ── Title ─────────────────────────────────────────────────────── */}
      <div aria-hidden style={{
        position: 'absolute', top: 18, left: 24,
        fontSize: 11, fontWeight: 700, letterSpacing: '2.5px',
        color: 'rgba(244,241,232,0.55)', textTransform: 'uppercase',
        fontFamily: 'monospace',
      }}>
        Oscar · Knowledge Atelier
      </div>

      {/* ── Oscar recipient figure (always visible at stage-right, sleeping until beat 3) ─ */}
      <div aria-hidden style={{
        position: 'absolute', right: 30, bottom: 110,
        opacity: beat >= 3 ? 1 : 0.35,
        transition: 'opacity 700ms ease',
        filter: beat >= 3 ? 'drop-shadow(0 0 22px rgba(198,167,90,0.45))' : 'none',
      }}>
        <KbAgentFigure kind="curator" size={130} />
        <div style={{ textAlign: 'center', marginTop: 4,
          fontSize: 10, fontWeight: 700, letterSpacing: '1.8px',
          color: GOLD, textTransform: 'uppercase', fontFamily: 'monospace' }}>
          Oscar
        </div>
      </div>

      {/* ── Upper text plate: user's typed lines ──────────────────────── */}
      <div style={{
        position: 'absolute', top: 64, left: 60, right: 220,
        padding: '24px 28px', borderRadius: 12,
        background: 'linear-gradient(180deg, rgba(244,241,232,0.04), rgba(244,241,232,0.01))',
        border: '1px solid rgba(244,241,232,0.08)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
        display: 'flex', flexDirection: 'column', gap: 8,
        minHeight: 140,
      }}>
        {trackA.map((entry, i) => (
          <KbRevealLine key={i} entry={entry} beat={beat}
            chipsLifted={beat >= 1} lineIdx={i} />
        ))}
      </div>

      {/* ── Plinth row (destinations) ─────────────────────────────────── */}
      <div data-testid="kb-reveal-bins" style={{
        position: 'absolute', left: 60, right: 220, bottom: 130,
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(Math.max(trackA.length, 1), 4)}, 1fr)`,
        gap: 16,
        opacity: beat >= 2 ? 1 : 0,
        transform: `translateY(${beat >= 2 ? 0 : 14}px)`,
        transition: 'opacity 600ms ease, transform 600ms ease',
        pointerEvents: 'none',
      }}>
        {bins.map((b, i) => (
          <KbRevealPlinth key={b.key} bin={b}
            received={beat === 2 ? currentChip >= i : beat > 2}
            morphedToCard={beat >= 3 && currentCard >= i} />
        ))}
      </div>

      {/* ── Card row (Beat 3) — plinths morph upward into cards ───────── */}
      <div data-testid="kb-reveal-cards" style={{
        position: 'absolute', left: 60, right: 220, top: 280,
        display: 'flex', flexDirection: 'column', gap: 10,
        opacity: beat >= 3 ? 1 : 0,
        transform: `translateY(${beat >= 3 ? 0 : 14}px)`,
        transition: 'opacity 600ms ease, transform 600ms ease',
        pointerEvents: 'none',
      }}>
        {trackA.map((t, i) => (
          <KbRevealCard key={i} fact={facts[i]}
            beat={beat}
            assembled={beat === 3 && currentCard >= i}
            index={i} />
        ))}
      </div>

      {/* ── Reader agent (Beat 1) — left of text plate ────────────────── */}
      <div aria-hidden style={{
        position: 'absolute', top: 86, left: 22, width: 120,
        opacity: readerOpacity,
        transform: `${readerLeft}`,
        transition: 'transform 700ms cubic-bezier(0.165,0.84,0.44,1), opacity 500ms ease',
        filter: 'drop-shadow(0 8px 18px rgba(126,217,242,0.18))',
      }}>
        <KbAgentFigure kind="reader" size={140} />
      </div>

      {/* ── Curator agent (Beat 2) — center stage ─────────────────────── */}
      <div aria-hidden style={{
        position: 'absolute',
        bottom: 130, left: '50%',
        opacity: curatorOpacity,
        transform: `translateX(-50%) ${curatorLeft}`,
        transition: 'transform 700ms cubic-bezier(0.165,0.84,0.44,1), opacity 500ms ease',
        filter: 'drop-shadow(0 12px 22px rgba(126,217,242,0.20))',
      }}>
        <KbAgentFigure kind="curator" size={150} carrying={beat === 2 && currentChip >= 0} />
      </div>

      {/* ── Structurer agent (Beat 3) — left-of-card area ─────────────── */}
      <div aria-hidden style={{
        position: 'absolute', top: 240, left: 16,
        opacity: structurerOp,
        transform: structurerLeft,
        transition: 'transform 700ms cubic-bezier(0.165,0.84,0.44,1), opacity 500ms ease',
        filter: 'drop-shadow(0 10px 22px rgba(198,167,90,0.20))',
      }}>
        <KbAgentFigure kind="structurer" size={140} />
      </div>

      {/* ── Gold ribbon to Oscar (Beat 3) — bold, deliberate, NOT a thin line ─ */}
      {beat === 3 && currentCard >= 0 && (
        <svg aria-hidden style={{
          position: 'absolute', right: 0, top: 280, height: 360, width: 220,
          pointerEvents: 'none',
        }}>
          <defs>
            <linearGradient id="kbRibbon" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="#C6A75A" stopOpacity="0.9"/>
              <stop offset="50%" stopColor="#FFE9A8" stopOpacity="0.95"/>
              <stop offset="100%" stopColor="#9D7E3E" stopOpacity="0.9"/>
            </linearGradient>
          </defs>
          {trackA.slice(0, currentCard + 1).map((_, i) => (
            <path key={i}
              d={`M 0 ${30 + i * 80} Q 100 ${20 + i * 80} 200 280`}
              fill="none" stroke="url(#kbRibbon)" strokeWidth="3"
              opacity={0.9 - i * 0.07}
              style={{
                strokeDasharray: '480',
                strokeDashoffset: '0',
                animation: `kbRibbonDraw 700ms ease-out`,
                animationDelay: `${i * 80}ms`,
              }} />
          ))}
        </svg>
      )}

      <style>{`
        @keyframes kbRibbonDraw {
          from { stroke-dashoffset: 480; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes kbChipRise {
          from { transform: translateY(0) scale(0.97); opacity: 0; }
          to   { transform: translateY(-44px) scale(1); opacity: 1; }
        }
        @keyframes kbScanSweep {
          from { left: 0%;   opacity: 0; }
          15%  { opacity: 0.95; }
          to   { left: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// One line of the user's typed text. Shows the user's actual line; as the
// beat advances, marked phrases lift visibly off the line into chips above.
const KbRevealLine = ({ entry, beat, lineIdx }) => {
  const { line, phrases } = entry;
  const segments = [];
  let cursor = 0;
  for (const p of phrases) {
    if (p.start > cursor) segments.push({ kind: 'text', text: line.slice(cursor, p.start) });
    segments.push({ kind: 'chip', text: line.slice(p.start, p.end), pkind: p.kind });
    cursor = p.end;
  }
  if (cursor < line.length) segments.push({ kind: 'text', text: line.slice(cursor) });

  const lifted = beat >= 1;

  return (
    <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap',
      gap: 0, alignItems: 'baseline',
      fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 22,
      color: '#F4F1E8', lineHeight: 1.55, letterSpacing: '0.2px',
      paddingTop: 4, paddingBottom: 4, minHeight: 30 }}>
      {/* Cyan reader scanline that sweeps once when Beat 1 starts */}
      {beat === 1 && (
        <span aria-hidden style={{
          position: 'absolute', top: 0, bottom: 0, width: 2,
          background: 'linear-gradient(180deg, rgba(126,217,242,0), rgba(126,217,242,0.85), rgba(126,217,242,0))',
          boxShadow: '0 0 12px rgba(126,217,242,0.6), 0 0 24px rgba(126,217,242,0.3)',
          animation: `kbScanSweep 1100ms ease-in-out forwards`,
          animationDelay: `${lineIdx * 220}ms`,
        }} />
      )}
      {segments.map((seg, idx) => {
        if (seg.kind === 'text') return <span key={idx}>{seg.text}</span>;
        return (
          <span key={idx} style={{ position: 'relative',
            display: 'inline-block', whiteSpace: 'pre' }}>
            {/* The original phrase becomes transparent + gold-underlined as it lifts. */}
            <span style={{
              display: 'inline-block',
              color: lifted ? 'transparent' : '#F4F1E8',
              borderBottom: lifted ? `1px solid ${GOLD}` : '1px solid transparent',
              transition: 'color 380ms ease, border-color 380ms ease',
              transitionDelay: `${lineIdx * 220 + idx * 90}ms`,
            }}>{seg.text}</span>
            {/* Chip floating above, with the user's literal characters. */}
            <span aria-hidden style={{
              position: 'absolute',
              top: lifted ? -42 : 0,
              left: 0,
              padding: '4px 10px',
              borderRadius: 6,
              background: 'linear-gradient(180deg, rgba(198,167,90,0.18), rgba(198,167,90,0.08))',
              border: `1px solid ${GOLD}`,
              color: GOLD,
              fontSize: 13,
              fontFamily: 'inherit',
              letterSpacing: '0.3px',
              boxShadow: lifted ? '0 4px 16px rgba(198,167,90,0.22), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
              opacity: lifted ? 1 : 0,
              transform: lifted ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
              transition: 'all 480ms cubic-bezier(0.165,0.84,0.44,1)',
              transitionDelay: `${lineIdx * 220 + idx * 90 + 180}ms`,
              whiteSpace: 'pre',
              pointerEvents: 'none',
            }}>{seg.text}</span>
          </span>
        );
      })}
    </div>
  );
};

// Plinth — destination "pedestal" that catches a chip and reveals its real
// topic name in serif once the chip lands.
const KbRevealPlinth = ({ bin, received, morphedToCard }) => {
  const labelReady = !!bin.label;
  const opacity    = morphedToCard ? 0.25 : 1;
  return (
    <div style={{
      position: 'relative',
      borderRadius: 12,
      padding: '14px 12px 16px',
      background: received
        ? 'linear-gradient(180deg, rgba(198,167,90,0.10), rgba(198,167,90,0.02))'
        : 'rgba(244,241,232,0.03)',
      border: `1px solid ${received ? 'rgba(198,167,90,0.55)' : 'rgba(244,241,232,0.16)'}`,
      transition: 'background 500ms ease, border-color 500ms ease, opacity 700ms ease',
      minHeight: 70,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      opacity,
      boxShadow: received
        ? '0 8px 22px rgba(198,167,90,0.18), inset 0 1px 0 rgba(255,255,255,0.06)'
        : 'inset 0 1px 0 rgba(255,255,255,0.04)',
    }}>
      {/* Chip glow when arriving */}
      {received && labelReady && (
        <div aria-hidden style={{
          position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
          width: 28, height: 4, borderRadius: 99,
          background: 'radial-gradient(circle, rgba(198,167,90,0.7), transparent)',
        }} />
      )}
      <div style={{
        fontFamily: 'Cormorant Garamond, Georgia, serif',
        fontSize: 18, fontWeight: 600, fontStyle: 'italic',
        color: labelReady ? '#F4F1E8' : 'rgba(244,241,232,0.30)',
        letterSpacing: '0.3px',
        transition: 'color 500ms ease',
      }}>
        {labelReady ? bin.label : '—'}
      </div>
      <div style={{
        fontSize: 9, fontFamily: 'monospace', letterSpacing: '1.4px',
        color: 'rgba(244,241,232,0.4)', textTransform: 'uppercase',
      }}>destination</div>
    </div>
  );
};

// Card — once the Structurer reaches it, fields paint in deliberately.
const KbRevealCard = ({ fact, beat, assembled, index }) => {
  if (!fact) {
    return (
      <div style={{
        border: '1px solid rgba(244,241,232,0.12)', borderRadius: 10,
        padding: '10px 14px', fontSize: 11, fontFamily: 'monospace',
        color: 'rgba(244,241,232,0.5)',
      }}>
        line {index + 1} — pending…
      </div>
    );
  }
  const conf = fact.confidence ?? 0.7;
  const lowConf = conf < 0.75;
  const accent = lowConf ? '#D9A24A' : GOLD;
  const tags = fact.tags || [];
  const qps  = fact.question_patterns || [];

  return (
    <div data-testid={`kb-reveal-card-${index}`} style={{
      border: `1px solid ${assembled ? accent : 'rgba(244,241,232,0.18)'}`,
      borderRadius: 12,
      padding: '12px 16px',
      background: assembled
        ? 'linear-gradient(180deg, rgba(198,167,90,0.06), rgba(244,241,232,0.04))'
        : 'rgba(244,241,232,0.03)',
      display: 'flex', flexDirection: 'column', gap: 8,
      transition: 'border-color 600ms ease, background 600ms ease',
      boxShadow: assembled
        ? '0 6px 18px rgba(198,167,90,0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
        : 'inset 0 1px 0 rgba(255,255,255,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, fontFamily: 'monospace',
          letterSpacing: '1.3px', textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: 99,
          background: lowConf ? 'rgba(217,162,74,0.16)' : 'rgba(198,167,90,0.16)',
          color: accent,
          border: `1px solid ${lowConf ? 'rgba(217,162,74,0.4)' : 'rgba(198,167,90,0.4)'}`,
          opacity: assembled ? 1 : 0,
          transition: 'opacity 280ms ease',
        }}>
          {KB_TOPIC_LABEL[fact.topic] || fact.topic_label || 'General'}
        </span>
        {tags.slice(0, 5).map((t, i) => (
          <span key={i} style={{
            fontSize: 9.5, color: 'rgba(244,241,232,0.72)',
            padding: '2px 7px', borderRadius: 99,
            background: 'rgba(244,241,232,0.05)',
            border: '1px solid rgba(244,241,232,0.15)',
            opacity: assembled ? 1 : 0,
            transform: assembled ? 'translateY(0)' : 'translateY(4px)',
            transition: 'opacity 280ms ease, transform 280ms ease',
            transitionDelay: `${i * 80 + 240}ms`,
          }}>{t}</span>
        ))}
      </div>
      <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
        fontSize: 17, color: '#F4F1E8', lineHeight: 1.5 }}>
        {fact.canonical_answer}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {qps.map((q, i) => (
          <div key={i} style={{
            fontSize: 11, fontStyle: 'italic',
            color: 'rgba(244,241,232,0.65)',
            opacity: assembled ? 1 : 0,
            transform: assembled ? 'translateY(0)' : 'translateY(4px)',
            transition: 'opacity 380ms ease, transform 380ms ease',
            transitionDelay: `${i * 200 + 600}ms`,
          }}>
            • {q}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── KbReviewCards — Screen 3: editable result with Advanced disclosure ───
const KbReviewCards = ({ structured, onCancel, onSaveAll, topicsAllowed }) => {
  const initial = (structured?.facts || []).map((f, i) => ({
    id:               null,
    raw_text:         f.raw_text,
    topic:            f.topic || 'general',
    topic_label:      KB_TOPIC_LABEL[f.topic] || f.topic_label || 'General',
    canonical_answer: f.canonical_answer || f.raw_text,
    tags:             Array.isArray(f.tags) ? [...f.tags] : [],
    question_pattern: (f.question_patterns && f.question_patterns[0]) || (f.raw_text + '?'),
    auxiliary_qps:    (f.question_patterns || []).slice(1),
    confidence:       typeof f.confidence === 'number' ? f.confidence : 0.7,
    ambiguity_reason: f.ambiguity_reason || null,
    skipped:          false,
    advancedOpen:     false,
    _idx:             i,
  }));
  const [cards, setCards] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updateCard = (idx, patch) => {
    setCards(cs => cs.map(c => c._idx === idx ? { ...c, ...patch } : c));
  };
  const toggleSkip = (idx) => updateCard(idx, { skipped: !cards.find(c=>c._idx===idx).skipped });
  const toggleAdvanced = (idx) => {
    const cur = cards.find(c=>c._idx===idx);
    updateCard(idx, { advancedOpen: !cur.advancedOpen });
  };

  const onSubmit = async (status) => {
    setSaving(true);
    setError(null);
    const adminAuth = { ...adminAuthHeader(), 'Content-Type': 'application/json' };
    const toSave = cards.filter(c => !c.skipped);
    try {
      let firstFactId = null;
      for (const c of toSave) {
        const allTags = [...new Set([...(c.tags||[]), ...(c.auxiliary_qps||[]).map(q => q.toLowerCase().slice(0, 60))])].filter(Boolean).slice(0, 20);
        const payload = {
          topic:            c.topic,
          question_pattern: c.question_pattern,
          canonical_answer: c.canonical_answer,
          tags:             allTags,
          status,
          source:           'composer',
        };
        const r = await fetch(`${API}/api/kb/facts`, { method: 'POST', headers: adminAuth, body: JSON.stringify(payload) });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${r.status}`);
        }
        if (!firstFactId) {
          const saved = await r.json().catch(() => ({}));
          if (saved?.id) firstFactId = saved.id;
        }
      }
      onSaveAll(toSave.length, status, firstFactId);
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const acceptedCount = cards.filter(c => !c.skipped).length;
  const lowConfCount  = cards.filter(c => !c.skipped && c.confidence < 0.75).length;

  return (
    <div data-testid="oscar-subtab-panel-kb"
      style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
        background: KB_PANEL_BG, padding: '24px 28px', display: 'flex',
        flexDirection: 'column', gap: 16 }}>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: GOLD_DK, marginBottom: 4 }}>
            Oscar understood
          </div>
          <h3 style={{ margin: 0, fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 24, fontWeight: 700, color: DARK, letterSpacing: '-0.2px' }}>
            {acceptedCount} {acceptedCount === 1 ? 'fact' : 'facts'} ready
            {lowConfCount > 0 && (
              <span style={{ fontSize: 12, color: '#D9A24A', fontWeight: 600,
                marginLeft: 12, letterSpacing: '0.4px', textTransform: 'uppercase',
                fontFamily: 'inherit' }}>
                {lowConfCount} need{lowConfCount === 1 ? 's' : ''} review
              </span>
            )}
          </h3>
        </div>
        <button data-testid="kb-review-cancel" onClick={onCancel}
          style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
            background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
          Discard all
        </button>
      </div>

      {error && (
        <div data-testid="kb-review-error"
          style={{ padding: '10px 12px', borderRadius: 10,
            background: 'rgba(178,58,72,0.08)', border: '1px solid rgba(178,58,72,0.25)',
            fontSize: 12, color: '#B23A48' }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {cards.map(c => (
          <KbReviewCard key={c._idx}
            card={c}
            topicsAllowed={topicsAllowed}
            onUpdate={(patch) => updateCard(c._idx, patch)}
            onToggleSkip={() => toggleSkip(c._idx)}
            onToggleAdvanced={() => toggleAdvanced(c._idx)} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: `1px solid ${KB_PANEL_BORDER}` }}>
        <button data-testid="kb-review-save-drafts" onClick={() => onSubmit('draft')}
          disabled={saving || acceptedCount === 0}
          style={{ padding: '10px 18px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
            background: '#FFFFFF', color: DARK, fontSize: 12, fontWeight: 700,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            cursor: saving || acceptedCount === 0 ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving…' : 'Save as drafts'}
        </button>
        <button data-testid="kb-review-publish-all" onClick={() => onSubmit('published')}
          disabled={saving || acceptedCount === 0}
          style={{ padding: '10px 22px', borderRadius: 10, border: 'none',
            background: saving || acceptedCount === 0
              ? 'rgba(0,0,0,0.06)'
              : `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
            color: saving || acceptedCount === 0 ? MUTED : '#1B1812',
            fontSize: 12, fontWeight: 800,
            letterSpacing: '0.7px', textTransform: 'uppercase',
            cursor: saving || acceptedCount === 0 ? 'not-allowed' : 'pointer',
            boxShadow: saving || acceptedCount === 0 ? 'none' : '0 3px 12px rgba(198,167,90,0.30)' }}>
          {saving ? 'Saving…' : 'Save & publish all'}
        </button>
      </div>
    </div>
  );
};

const KbReviewCard = ({ card, topicsAllowed, onUpdate, onToggleSkip, onToggleAdvanced }) => {
  const lowConf = card.confidence < 0.75;
  const accent = lowConf ? '#D9A24A' : GOLD;
  return (
    <div style={{
      border: `1px solid ${card.skipped ? 'rgba(0,0,0,0.10)' : (lowConf ? 'rgba(217,162,74,0.4)' : 'rgba(198,167,90,0.32)')}`,
      borderRadius: 12,
      padding: '14px 18px',
      background: card.skipped ? 'rgba(0,0,0,0.02)' : (lowConf ? 'rgba(217,162,74,0.04)' : 'rgba(198,167,90,0.04)'),
      opacity: card.skipped ? 0.5 : 1,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select data-testid={`kb-card-${card._idx}-topic`} value={card.topic}
          onChange={e => onUpdate({ topic: e.target.value, topic_label: KB_TOPIC_LABEL[e.target.value] || e.target.value })}
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
            textTransform: 'uppercase', padding: '3px 6px', borderRadius: 99,
            background: lowConf ? 'rgba(217,162,74,0.14)' : 'rgba(198,167,90,0.14)',
            color: accent,
            border: `1px solid ${accent}`,
            cursor: 'pointer', outline: 'none',
          }}>
          {(topicsAllowed || KB_TOPIC_FALLBACK).map(t => (
            <option key={t} value={t}>{KB_TOPIC_LABEL[t] || t}</option>
          ))}
        </select>
        {card.tags.slice(0, 5).map((t, i) => (
          <span key={i} style={{
            fontSize: 10, color: MUTED,
            padding: '2px 7px', borderRadius: 99,
            background: '#FAFAFB', border: `1px solid ${KB_PANEL_BORDER}` }}>{t}</span>
        ))}
        <span style={{ flex: 1 }} />
        {lowConf && (
          <span style={{ fontSize: 10, color: '#D9A24A', fontWeight: 700,
            letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            review me
          </span>
        )}
        <span style={{ fontSize: 10, fontFamily: 'monospace', color: MUTED }}>
          {Math.round(card.confidence * 100)}%
        </span>
      </div>
      <textarea data-testid={`kb-card-${card._idx}-answer`}
        value={card.canonical_answer}
        onChange={e => onUpdate({ canonical_answer: e.target.value })}
        rows={Math.max(2, Math.ceil(card.canonical_answer.length / 80))}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
          border: `1px solid ${KB_PANEL_BORDER}`, background: '#FFFFFF',
          fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 16,
          color: DARK, lineHeight: 1.55, outline: 'none', resize: 'vertical' }} />

      <div>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
          textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>
          Guests might ask
        </div>
        <div style={{ fontSize: 12, fontStyle: 'italic', color: MUTED, lineHeight: 1.6 }}>
          {card.question_pattern}
          {card.auxiliary_qps.length > 0 && (
            <>
              {card.auxiliary_qps.map((q, i) => (
                <div key={i}>{q}</div>
              ))}
            </>
          )}
        </div>
      </div>

      {card.ambiguity_reason && !card.skipped && (
        <div style={{ fontSize: 11, color: '#D9A24A', fontStyle: 'italic',
          padding: '6px 10px', borderRadius: 8,
          background: 'rgba(217,162,74,0.08)',
          border: '1px solid rgba(217,162,74,0.25)' }}>
          {card.ambiguity_reason}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 4 }}>
        <button data-testid={`kb-card-${card._idx}-skip`} onClick={onToggleSkip}
          style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${KB_PANEL_BORDER}`,
            background: '#FFFFFF', color: MUTED, fontSize: 10, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
          {card.skipped ? 'Restore' : 'Skip this one'}
        </button>
        <button data-testid={`kb-card-${card._idx}-advanced`} onClick={onToggleAdvanced}
          style={{ padding: '5px 10px', borderRadius: 8, border: 'none',
            background: 'transparent', color: MUTED, fontSize: 10, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
          {card.advancedOpen ? '▾ Advanced' : '▸ Advanced'}
        </button>
      </div>

      {card.advancedOpen && (
        <div style={{ paddingTop: 8, borderTop: `1px dashed ${KB_PANEL_BORDER}`,
          display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
              textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
              Question pattern
            </label>
            <input type="text" value={card.question_pattern}
              onChange={e => onUpdate({ question_pattern: e.target.value })}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 8,
                border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
                fontSize: 12, color: DARK, outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
              textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
              Tags (comma separated)
            </label>
            <input type="text" value={(card.tags || []).join(', ')}
              onChange={e => onUpdate({ tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 8,
                border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
                fontSize: 12, color: DARK, outline: 'none' }} />
          </div>
        </div>
      )}
    </div>
  );
};

const OscarKnowledgeBasePanel = ({ kbBootstrap, onBootstrapConsumed }) => {
  const [facts, setFacts]         = useState([]);
  const [totals, setTotals]       = useState({ draft: 0, published: 0, all: 0 });
  const [topicsAllowed, setTopics] = useState(KB_TOPIC_FALLBACK);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');   // 'all' | 'draft' | 'published'
  const [topicFilter, setTopicFilter]   = useState('all');
  const [search, setSearch]       = useState('');
  const [editing, setEditing]     = useState(null);          // fact obj being edited; null when picker open
  const [creating, setCreating]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);  // fact id pending delete confirm

  // ── Composer / Reveal / Review state (Phase 2D AI-assisted authoring) ──
  const [composerMode, setComposerMode]       = useState('list'); // 'list' | 'compose' | 'reveal' | 'review'
  const [composerText, setComposerText]       = useState('');
  const [revealTrackA, setRevealTrackA]       = useState([]);
  const [revealStructPromise, setRevealPromise] = useState(null);
  const [structuringResult, setStructuringResult] = useState(null);
  const [composerToast, setComposerToast]     = useState(null); // {n, status} after save
  // Phase 3B — when this is set, after the next successful save we POST to
  // mark the originating unknown question as resolved + linked to the fact.
  const [pendingUnknownId, setPendingUnknownId] = useState(null);

  const adminAuth = adminAuthHeader();

  // Promote-to-KB bootstrap from the Unknown subtab.
  useEffect(() => {
    if (!kbBootstrap) return;
    setComposerToast(null);
    setComposerText(kbBootstrap.text || '');
    setPendingUnknownId(kbBootstrap.unknownId || null);
    setComposerMode('compose');
    onBootstrapConsumed?.();
    // We deliberately don't include onBootstrapConsumed in deps — calling it
    // is a one-shot signal back to the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kbBootstrap]);

  const fetchFacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (topicFilter  !== 'all') params.set('topic',  topicFilter);
      if (search.trim())          params.set('q',      search.trim());
      const r = await fetch(`${API}/api/kb/facts?${params.toString()}`, { headers: adminAuth });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setFacts(Array.isArray(d.facts) ? d.facts : []);
      setTotals(d.totals || { draft: 0, published: 0, all: 0 });
      if (Array.isArray(d.topics_allowed) && d.topics_allowed.length) {
        setTopics(d.topics_allowed);
      }
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
  }, [statusFilter, topicFilter, search]);

  useEffect(() => { fetchFacts(); }, [fetchFacts]);

  const startCreate = () => {
    setEditing({
      id: null,
      topic: 'general',
      question_pattern: '',
      canonical_answer: '',
      tags: [],
      status: 'draft',
    });
    setCreating(true);
  };

  const startEdit = (fact) => {
    setEditing({ ...fact, tags: Array.isArray(fact.tags) ? [...fact.tags] : [] });
    setCreating(false);
  };

  const closeEditor = () => {
    setEditing(null);
    setCreating(false);
  };

  const onSave = async () => {
    if (!editing) return;
    const payload = {
      topic:            (editing.topic || '').trim().toLowerCase(),
      question_pattern: (editing.question_pattern || '').trim(),
      canonical_answer: (editing.canonical_answer || '').trim(),
      tags:             (editing.tags || []).map(t => (t || '').trim().toLowerCase()).filter(Boolean),
    };
    if (!payload.topic || !payload.question_pattern || !payload.canonical_answer) {
      setError('Topic, question pattern, and canonical answer are all required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const url = editing.id
        ? `${API}/api/kb/facts/${editing.id}`
        : `${API}/api/kb/facts`;
      const method = editing.id ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { ...adminAuth, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      closeEditor();
      await fetchFacts();
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const onTogglePublish = async (fact) => {
    setSaving(true);
    setError(null);
    try {
      const action = fact.status === 'published' ? 'unpublish' : 'publish';
      const r = await fetch(`${API}/api/kb/facts/${fact.id}/${action}`, {
        method: 'POST',
        headers: adminAuth,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchFacts();
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  const onDelete = async (fact_id) => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/kb/facts/${fact_id}`, {
        method: 'DELETE',
        headers: adminAuth,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setConfirmDelete(null);
      await fetchFacts();
    } catch (e) {
      setError(String(e.message || e));
    }
    setSaving(false);
  };

  // ── Composer flow handlers ────────────────────────────────────────────
  const startComposer = () => {
    setComposerToast(null);
    setComposerMode('compose');
  };

  const onComposerCancel = () => {
    setComposerMode('list');
    setComposerText('');
    setRevealTrackA([]);
    setRevealPromise(null);
    setStructuringResult(null);
  };

  const onComposerContinue = (text) => {
    setComposerText(text);
    const trackA = kbTrackAExtract(text);
    setRevealTrackA(trackA);
    // Fire Track B in parallel with the choreography. The Reveal component
    // does not block on it; it merely awaits this promise.
    const p = fetch(`${API}/api/kb/structure`, {
      method: 'POST',
      headers: { ...adminAuth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
    setRevealPromise(p);
    setComposerMode('reveal');
  };

  const onRevealComplete = (structured) => {
    setStructuringResult(structured);
    setComposerMode('review');
  };

  const onReviewSaveAll = async (n, status, firstFactId) => {
    // If this save originated from the Unknown queue, mark that question
    // resolved. We don't link a specific fact id (the user may have produced
    // multiple cards) — the resolved flag is sufficient for the queue.
    const idToResolve = pendingUnknownId;
    setComposerMode('list');
    setComposerText('');
    setRevealTrackA([]);
    setRevealPromise(null);
    setStructuringResult(null);
    setPendingUnknownId(null);
    setComposerToast({ n, status, fromUnknown: !!idToResolve });
    setTimeout(() => setComposerToast(null), 5000);
    if (idToResolve) {
      try {
        await fetch(`${API}/api/oscar/unknown-questions/${idToResolve}/mark-resolved`, {
          method: 'POST',
          headers: { ...adminAuth, 'Content-Type': 'application/json' },
          body: JSON.stringify(firstFactId ? { approved_kb_id: firstFactId } : {}),
        });
      } catch (_) { /* non-blocking */ }
    }
    await fetchFacts();
  };

  // ── Composer flow render branches ──────────────────────────────────────
  if (composerMode === 'compose') {
    return (
      <KbComposer initialText={composerText}
        onCancel={onComposerCancel}
        onContinue={onComposerContinue} />
    );
  }
  if (composerMode === 'reveal') {
    return (
      <KbReveal rawText={composerText} trackA={revealTrackA}
        structuringPromise={revealStructPromise}
        onComplete={onRevealComplete}
        onCancel={onComposerCancel} />
    );
  }
  if (composerMode === 'review') {
    return (
      <KbReviewCards structured={structuringResult}
        topicsAllowed={topicsAllowed}
        onCancel={onComposerCancel}
        onSaveAll={onReviewSaveAll} />
    );
  }

  // ── Editor view ───────────────────────────────────────────────────────────
  if (editing) {
    const isNew = !editing.id;
    return (
      <div data-testid="oscar-subtab-panel-kb"
        style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
          background: KB_PANEL_BG, padding: '24px 26px', display: 'flex',
          flexDirection: 'column', gap: 18 }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
              {isNew ? 'New KB Fact' : 'Edit KB Fact'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
              {isNew
                ? 'Draft a guest-safe answer Oscar may quote verbatim once published.'
                : <>Version <strong>{editing.version || 1}</strong> · last updated {editing.updated_at ? fmtRelative(editing.updated_at) : '—'}</>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-testid="kb-editor-cancel" onClick={closeEditor}
              style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
                background: '#FFFFFF', color: DARK, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', letterSpacing: '0.3px' }}>
              Cancel
            </button>
            <button data-testid="kb-editor-save" onClick={onSave} disabled={saving}
              style={{ padding: '8px 18px', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
                color: '#1B1812', fontSize: 12, fontWeight: 700, letterSpacing: '0.4px',
                textTransform: 'uppercase', cursor: saving ? 'wait' : 'pointer',
                boxShadow: '0 2px 8px rgba(198,167,90,0.30)' }}>
              {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="kb-editor-error"
            style={{ padding: '10px 12px', borderRadius: 10,
              background: 'rgba(178,58,72,0.08)', border: '1px solid rgba(178,58,72,0.25)',
              fontSize: 12, color: '#B23A48' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
              textTransform: 'uppercase', color: MUTED, marginBottom: 6, display: 'block' }}>
              Topic <span style={{ color: '#B23A48' }}>*</span>
            </label>
            <select data-testid="kb-editor-topic" value={editing.topic || 'general'}
              onChange={e => setEditing({ ...editing, topic: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 10,
                border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
                fontSize: 13, color: DARK, outline: 'none', fontFamily: 'inherit' }}>
              {topicsAllowed.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
              textTransform: 'uppercase', color: MUTED, marginBottom: 6, display: 'block' }}>
              Tags (comma separated)
            </label>
            <input data-testid="kb-editor-tags" type="text"
              value={(editing.tags || []).join(', ')}
              onChange={e => setEditing({ ...editing, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              placeholder="e.g. host_hotel, shuttle"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 10,
                border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
                fontSize: 13, color: DARK, outline: 'none', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div>
          <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 6, display: 'block' }}>
            Question pattern <span style={{ color: '#B23A48' }}>*</span>
          </label>
          <input data-testid="kb-editor-question" type="text"
            value={editing.question_pattern || ''}
            onChange={e => setEditing({ ...editing, question_pattern: e.target.value })}
            placeholder="e.g. Where should I stay?"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 13, color: DARK, outline: 'none', fontFamily: 'inherit' }} />
          <div style={{ fontSize: 11, color: MUTED, marginTop: 4, fontStyle: 'italic' }}>
            What guest question this answers. Free text — Phase 2D will use this for retrieval.
          </div>
        </div>

        <div>
          <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 6, display: 'block' }}>
            Canonical answer <span style={{ color: '#B23A48' }}>*</span>
          </label>
          <textarea data-testid="kb-editor-answer" rows={6}
            value={editing.canonical_answer || ''}
            onChange={e => setEditing({ ...editing, canonical_answer: e.target.value })}
            placeholder="The trusted answer Oscar may quote verbatim once published."
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 13, color: DARK, outline: 'none', fontFamily: 'inherit',
              resize: 'vertical', lineHeight: 1.55 }} />
          <div style={{ fontSize: 11, color: MUTED, marginTop: 4, fontStyle: 'italic' }}>
            Up to 4000 characters. This is what Oscar will be allowed to say verbatim.
          </div>
        </div>

        <div style={{ paddingTop: 8, borderTop: `1px solid ${KB_PANEL_BORDER}`,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={12} color={MUTED} />
          <span style={{ fontSize: 11, color: MUTED, lineHeight: 1.55 }}>
            Saving creates this fact in <strong>draft</strong> by default. Use <strong>Publish</strong> from the list view to make it available to Oscar (Phase 2D).
          </span>
        </div>
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div data-testid="oscar-subtab-panel-kb"
      style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
        background: KB_PANEL_BG, padding: '22px 24px', display: 'flex',
        flexDirection: 'column', gap: 16 }}>

      {composerToast && (
        <div data-testid="kb-composer-toast"
          style={{ padding: '10px 14px', borderRadius: 10,
            background: 'rgba(30,115,68,0.08)',
            border: '1px solid rgba(30,115,68,0.30)',
            fontSize: 12, color: '#1E7344', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.7px',
            textTransform: 'uppercase', padding: '2px 7px', borderRadius: 99,
            background: 'rgba(30,115,68,0.16)' }}>
            {composerToast.status === 'published' ? 'Published' : 'Saved'}
          </span>
          {composerToast.n} {composerToast.n === 1 ? 'fact' : 'facts'} {composerToast.status === 'published' ? 'published' : 'saved as drafts'}
          {composerToast.fromUnknown ? ' — promoted question marked resolved.' : '.'}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
              Knowledge Base
            </h3>
            <StatusBadge>Phase 2C · Live</StatusBadge>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55, maxWidth: 620 }}>
            Curate the answers Oscar is allowed to quote verbatim. Drafts are private.
            Published facts will be retrievable by Oscar v2 (Phase 2D) — for now nothing
            is wired into the guest chat path. Admin-only.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button data-testid="kb-list-refresh" onClick={fetchFacts}
            style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
              background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
              letterSpacing: '0.4px', textTransform: 'uppercase', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button data-testid="kb-list-new" onClick={startComposer}
            style={{ padding: '9px 16px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
              color: '#1B1812', fontSize: 12, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(198,167,90,0.30)' }}>
            + Add facts
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div data-testid="kb-list-totals"
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: MUTED }}>
          <strong style={{ color: DARK, fontSize: 13 }}>{totals.all}</strong> total
        </span>
        <span style={{ fontSize: 11, color: '#1E7344' }}>
          <strong style={{ fontSize: 13 }}>{totals.published}</strong> published
        </span>
        <span style={{ fontSize: 11, color: MUTED }}>
          <strong style={{ color: DARK, fontSize: 13 }}>{totals.draft}</strong> draft
        </span>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 200px 1fr', gap: 10, alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
            Status
          </label>
          <select data-testid="kb-filter-status" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ width: '100%', padding: '7px 9px', borderRadius: 8,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 12, color: DARK, outline: 'none', fontFamily: 'inherit' }}>
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
            Topic
          </label>
          <select data-testid="kb-filter-topic" value={topicFilter}
            onChange={e => setTopicFilter(e.target.value)}
            style={{ width: '100%', padding: '7px 9px', borderRadius: 8,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 12, color: DARK, outline: 'none', fontFamily: 'inherit' }}>
            <option value="all">All topics</option>
            {topicsAllowed.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
            textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: MUTED }} />
            <input data-testid="kb-filter-search" type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Find in question or answer…"
              style={{ width: '100%', padding: '7px 10px 7px 28px', borderRadius: 8,
                border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
                fontSize: 12, color: DARK, outline: 'none', fontFamily: 'inherit' }} />
          </div>
        </div>
      </div>

      {error && !editing && (
        <div data-testid="kb-list-error"
          style={{ padding: '10px 12px', borderRadius: 10,
            background: 'rgba(178,58,72,0.08)', border: '1px solid rgba(178,58,72,0.25)',
            fontSize: 12, color: '#B23A48' }}>
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: MUTED, fontSize: 12 }}>
          Loading…
        </div>
      ) : facts.length === 0 ? (
        <div data-testid="kb-list-empty"
          style={{ padding: '40px 24px', textAlign: 'center', color: MUTED,
            border: `1px dashed ${KB_PANEL_BORDER}`, borderRadius: 12,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <BookOpen size={28} color={GOLD} strokeWidth={1.4} />
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>No facts yet</div>
          <div style={{ fontSize: 12, color: MUTED, maxWidth: 380, lineHeight: 1.55 }}>
            Drafts and published facts will appear here. Click <strong>+ Add facts</strong> to start curating Oscar's knowledge base.
          </div>
        </div>
      ) : (
        <div data-testid="kb-list-rows" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {facts.map((f, idx) => {
            const live = f.status === 'published';
            return (
              <div key={f.id} data-testid={`kb-row-${idx}`}
                style={{ display: 'flex', flexDirection: 'column', gap: 8,
                  padding: '14px 16px', borderRadius: 12,
                  background: live ? 'rgba(30,115,68,0.04)' : '#FFFFFF',
                  border: `1px solid ${live ? 'rgba(30,115,68,0.18)' : KB_PANEL_BORDER}`,
                  transition: 'border-color 0.15s ease' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <KbStatusBadge status={f.status} />
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.7px',
                    textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99,
                    background: 'rgba(198,167,90,0.12)', color: GOLD_DK,
                    border: '1px solid rgba(198,167,90,0.30)' }}>
                    {f.topic}
                  </span>
                  {(f.tags || []).slice(0, 4).map(t => (
                    <span key={t} style={{ fontSize: 10, color: MUTED,
                      padding: '2px 7px', borderRadius: 99,
                      background: 'rgba(0,0,0,0.04)', border: `1px solid ${KB_PANEL_BORDER}` }}>
                      {t}
                    </span>
                  ))}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>
                    v{f.version || 1} · {fmtRelative(f.updated_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: DARK, lineHeight: 1.45 }}>
                  {f.question_pattern}
                </div>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                  {truncate(f.canonical_answer, 280)}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button data-testid={`kb-row-${idx}-edit`} onClick={() => startEdit(f)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${KB_PANEL_BORDER}`,
                      background: '#FFFFFF', color: DARK, fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', letterSpacing: '0.3px' }}>
                    Edit
                  </button>
                  <button data-testid={`kb-row-${idx}-toggle-publish`} onClick={() => onTogglePublish(f)} disabled={saving}
                    style={{ padding: '6px 12px', borderRadius: 8, border: 'none',
                      background: live ? 'rgba(0,0,0,0.06)' : `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
                      color: live ? DARK : '#1B1812', fontSize: 11, fontWeight: 700,
                      cursor: saving ? 'wait' : 'pointer', letterSpacing: '0.4px',
                      textTransform: 'uppercase' }}>
                    {live ? 'Unpublish' : 'Publish'}
                  </button>
                  {confirmDelete === f.id ? (
                    <>
                      <button data-testid={`kb-row-${idx}-delete-confirm`} onClick={() => onDelete(f.id)} disabled={saving}
                        style={{ padding: '6px 12px', borderRadius: 8, border: 'none',
                          background: '#B23A48', color: '#FFFFFF', fontSize: 11, fontWeight: 700,
                          cursor: saving ? 'wait' : 'pointer', letterSpacing: '0.4px',
                          textTransform: 'uppercase' }}>
                        Confirm Delete
                      </button>
                      <button onClick={() => setConfirmDelete(null)}
                        style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${KB_PANEL_BORDER}`,
                          background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
                          cursor: 'pointer', letterSpacing: '0.3px' }}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button data-testid={`kb-row-${idx}-delete`} onClick={() => setConfirmDelete(f.id)}
                      style={{ padding: '6px 12px', borderRadius: 8,
                        border: '1px solid rgba(178,58,72,0.24)',
                        background: '#FFFFFF', color: '#B23A48', fontSize: 11, fontWeight: 600,
                        cursor: 'pointer', letterSpacing: '0.3px' }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── OscarUnknownPanel — Phase 3B: review escalated guest questions ────────
const OscarUnknownPanel = ({ onPromoteToKb }) => {
  const [items, setItems]   = useState([]);
  const [totals, setTotals] = useState({ pending: 0, dismissed: 0, resolved: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [statusTab, setStatusTab] = useState('pending');  // 'pending' | 'dismissed' | 'resolved'
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const adminAuth = adminAuthHeader();

  const fetchQueue = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ status: statusTab, limit: '500' });
      if (search.trim()) params.set('q', search.trim());
      const r = await fetch(`${API}/api/oscar/unknown-questions?${params.toString()}`, { headers: adminAuth });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(Array.isArray(d.items) ? d.items : []);
      setTotals(d.totals || { pending: 0, dismissed: 0, resolved: 0, all: 0 });
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, search]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const callAction = async (id, path) => {
    setBusyId(id);
    try {
      const r = await fetch(`${API}/api/oscar/unknown-questions/${id}/${path}`, {
        method: 'POST', headers: adminAuth,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchQueue();
    } catch (e) {
      setError(String(e.message || e));
    }
    setBusyId(null);
  };

  const onDismiss   = (id) => callAction(id, 'dismiss');
  const onRestore   = (id) => callAction(id, 'restore');
  const onPromote   = (item) => onPromoteToKb?.(item.question, item.id);

  const TAB_LABEL = { pending: 'Pending', dismissed: 'Dismissed', resolved: 'Resolved' };

  return (
    <div data-testid="oscar-subtab-panel-unknown"
      style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
        background: KB_PANEL_BG, padding: '22px 24px', display: 'flex',
        flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
              Unknown Questions
            </h3>
            <StatusBadge>Phase 3B · Live</StatusBadge>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55, maxWidth: 620 }}>
            Questions Oscar escalated because no published KB fact answered them.
            Promote good ones into the KB; dismiss noise. Recurrence count is
            live — repeated guest questions bump the same row.
          </p>
        </div>
        <button data-testid="unknown-refresh" onClick={fetchQueue}
          style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
            background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
            letterSpacing: '0.4px', textTransform: 'uppercase', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${KB_PANEL_BORDER}` }}>
        {['pending', 'dismissed', 'resolved'].map(s => {
          const active = statusTab === s;
          return (
            <button key={s} data-testid={`unknown-tab-${s}`}
              onClick={() => setStatusTab(s)}
              style={{
                padding: '8px 14px', border: 'none', background: 'transparent',
                color: active ? DARK : MUTED,
                fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
                textTransform: 'uppercase', cursor: 'pointer',
                borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                marginBottom: -1, transition: 'color 0.15s ease',
              }}>
              {TAB_LABEL[s]}
              <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>
                {totals[s] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div>
        <label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
          textTransform: 'uppercase', color: MUTED, marginBottom: 4, display: 'block' }}>
          Search
        </label>
        <div style={{ position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 10, top: '50%',
            transform: 'translateY(-50%)', color: MUTED }} />
          <input data-testid="unknown-search" type="text" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find within question text…"
            style={{ width: '100%', padding: '7px 10px 7px 28px', borderRadius: 8,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 12, color: DARK, outline: 'none', fontFamily: 'inherit' }} />
        </div>
      </div>

      {error && (
        <div data-testid="unknown-error"
          style={{ padding: '10px 12px', borderRadius: 10,
            background: 'rgba(178,58,72,0.08)', border: '1px solid rgba(178,58,72,0.25)',
            fontSize: 12, color: '#B23A48' }}>{error}</div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: MUTED, fontSize: 12 }}>
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="unknown-empty"
          style={{ padding: '40px 24px', textAlign: 'center', color: MUTED,
            border: `1px dashed ${KB_PANEL_BORDER}`, borderRadius: 12,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <HelpCircle size={28} color={GOLD} strokeWidth={1.4} />
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>
            {statusTab === 'pending'   ? 'No pending questions'
            : statusTab === 'dismissed' ? 'No dismissed questions'
            : 'No resolved questions yet'}
          </div>
          <div style={{ fontSize: 12, color: MUTED, maxWidth: 420, lineHeight: 1.55 }}>
            {statusTab === 'pending'
              ? 'When Oscar escalates a guest question that no published KB fact answers, it lands here for review.'
              : statusTab === 'dismissed'
              ? 'Dismissed questions are hidden from the pending queue but kept so future asks dedupe correctly.'
              : 'Questions you have promoted into the KB will appear here.'}
          </div>
        </div>
      ) : (
        <div data-testid="unknown-rows" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((row, idx) => (
            <UnknownRow key={row.id} row={row} idx={idx} busy={busyId === row.id}
              statusTab={statusTab}
              onPromote={() => onPromote(row)}
              onDismiss={() => onDismiss(row.id)}
              onRestore={() => onRestore(row.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

const UnknownRow = ({ row, idx, busy, statusTab, onPromote, onDismiss, onRestore }) => {
  const isHot = (row.count || 1) >= 3;
  return (
    <div data-testid={`unknown-row-${idx}`}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        padding: '14px 16px', borderRadius: 12,
        background: isHot ? 'rgba(217,162,74,0.04)' : '#FFFFFF',
        border: `1px solid ${isHot ? 'rgba(217,162,74,0.36)' : KB_PANEL_BORDER}`,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.7px',
          textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99,
          background: isHot ? 'rgba(217,162,74,0.16)' : 'rgba(0,0,0,0.05)',
          color: isHot ? '#9D6F1F' : MUTED,
          border: `1px solid ${isHot ? 'rgba(217,162,74,0.40)' : KB_PANEL_BORDER}` }}>
          {row.count || 1}× asked
        </span>
        {row.last_session_id && (
          <span style={{ fontSize: 9, fontFamily: 'monospace',
            color: MUTED, padding: '2px 7px', borderRadius: 99,
            background: '#FAFAFB', border: `1px solid ${KB_PANEL_BORDER}` }}>
            sid:{(row.last_session_id || '').slice(0, 14)}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>
          first {row.first_seen ? fmtRelative(row.first_seen) : '—'} · last {row.last_seen ? fmtRelative(row.last_seen) : '—'}
        </span>
      </div>
      <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
        fontSize: 17, color: DARK, lineHeight: 1.45 }}>
        “{row.question}”
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {statusTab === 'pending' && (
          <>
            <button data-testid={`unknown-row-${idx}-promote`} onClick={onPromote}
              disabled={busy}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none',
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})`,
                color: '#1B1812', fontSize: 11, fontWeight: 800,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                cursor: busy ? 'wait' : 'pointer',
                boxShadow: '0 2px 8px rgba(198,167,90,0.30)' }}>
              + Promote to KB
            </button>
            <button data-testid={`unknown-row-${idx}-dismiss`} onClick={onDismiss}
              disabled={busy}
              style={{ padding: '7px 12px', borderRadius: 8,
                border: `1px solid ${KB_PANEL_BORDER}`, background: '#FFFFFF',
                color: MUTED, fontSize: 11, fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer', letterSpacing: '0.3px' }}>
              Dismiss
            </button>
          </>
        )}
        {statusTab === 'dismissed' && (
          <button data-testid={`unknown-row-${idx}-restore`} onClick={onRestore}
            disabled={busy}
            style={{ padding: '7px 12px', borderRadius: 8,
              border: `1px solid ${KB_PANEL_BORDER}`, background: '#FFFFFF',
              color: DARK, fontSize: 11, fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer', letterSpacing: '0.3px' }}>
            Restore to pending
          </button>
        )}
        {statusTab === 'resolved' && (
          <>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
              textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99,
              background: 'rgba(30,115,68,0.10)', color: '#1E7344',
              border: '1px solid rgba(30,115,68,0.30)' }}>
              Resolved
            </span>
            {row.approved_kb_id && (
              <span style={{ fontSize: 9, fontFamily: 'monospace', color: MUTED }}>
                kb:{(row.approved_kb_id || '').slice(0, 12)}
              </span>
            )}
            <button data-testid={`unknown-row-${idx}-restore`} onClick={onRestore}
              disabled={busy}
              style={{ padding: '5px 10px', borderRadius: 8,
                border: `1px solid ${KB_PANEL_BORDER}`, background: '#FFFFFF',
                color: MUTED, fontSize: 10, fontWeight: 600,
                cursor: busy ? 'wait' : 'pointer', letterSpacing: '0.3px' }}>
              Restore
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── OscarHealthPanel — Phase 3C: read-only Activity & Health analytics ────
const HEALTH_WINDOWS = [
  { key: '7',  label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
];

const _fmtPct = (v, decimals = 1) =>
  v == null ? '—' : `${(v * 100).toFixed(decimals)}%`;
const _fmtMs  = (v) => v == null ? '—' : `${v.toLocaleString()} ms`;
const _fmtUsd = (v) => v == null ? '—' : `$${(+v).toFixed(4)}`;
const _fmtInt = (v) => v == null ? '—' : (+v).toLocaleString();

const HealthKpi = ({ label, value, hint, approximate, testid }) => (
  <div data-testid={testid}
    style={{
      flex: '1 1 160px', minWidth: 150,
      border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
      background: '#FFFFFF', padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.9px',
      textTransform: 'uppercase', color: MUTED, display: 'flex',
      alignItems: 'center', gap: 6 }}>
      {label}
      {approximate && (
        <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px',
          borderRadius: 99, background: 'rgba(217,162,74,0.14)',
          color: '#9D6F1F', border: '1px solid rgba(217,162,74,0.30)',
          letterSpacing: '0.4px' }}>
          ≈ approx
        </span>
      )}
    </div>
    <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
      fontSize: 26, fontWeight: 700, color: DARK, lineHeight: 1.1 }}>
      {value}
    </div>
    {hint && (
      <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>{hint}</div>
    )}
  </div>
);

const HealthBar = ({ label, count, total, color = GOLD, testid }) => {
  const pct = total > 0 ? (count / total) : 0;
  return (
    <div data-testid={testid} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, color: DARK, fontWeight: 600, letterSpacing: '0.2px' }}>
          {label}
        </span>
        <span style={{ fontSize: 11, color: MUTED, fontFamily: 'monospace' }}>
          {count.toLocaleString()}{' '}
          <span style={{ opacity: 0.7 }}>({(pct * 100).toFixed(1)}%)</span>
        </span>
      </div>
      <div style={{ height: 4, borderRadius: 99, background: 'rgba(0,0,0,0.05)',
        overflow: 'hidden' }}>
        <div style={{
          width: `${Math.max(2, pct * 100)}%`,
          height: '100%',
          background: color,
          transition: 'width 350ms ease',
        }} />
      </div>
    </div>
  );
};

const HealthDailyBars = ({ daily }) => {
  if (!daily || daily.length === 0) {
    return (
      <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic',
        padding: '20px 0' }}>
        No daily data in this window.
      </div>
    );
  }
  const maxTurns = Math.max(1, ...daily.map(d => d.turns || 0));
  return (
    <div data-testid="health-daily-bars"
      style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
      {daily.map((d, i) => {
        const h = Math.round((d.turns / maxTurns) * 70);
        const escalatedH = d.turns > 0
          ? Math.round((d.escalated / d.turns) * h)
          : 0;
        return (
          <div key={d.date}
            title={`${d.date} · ${d.turns} turns · ${d.escalated} escalated · $${(d.cost || 0).toFixed(4)}`}
            style={{ flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'stretch', justifyContent: 'flex-end',
              minWidth: 8, gap: 1 }}>
            <div style={{
              width: '100%', height: Math.max(2, h),
              background: 'linear-gradient(180deg, #C6A75A 0%, #9D7E3E 100%)',
              borderRadius: '2px 2px 0 0',
              position: 'relative',
            }}>
              {escalatedH > 0 && (
                <div style={{
                  position: 'absolute', left: 0, right: 0, bottom: 0,
                  height: escalatedH,
                  background: '#D9A24A',
                  borderRadius: '0 0 0 0',
                  opacity: 0.85,
                }} />
              )}
            </div>
            <div style={{ fontSize: 8, fontFamily: 'monospace', color: MUTED,
              textAlign: 'center', letterSpacing: '0.3px' }}>
              {d.date.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const OscarHealthPanel = () => {
  const [windowDays, setWindowDays] = useState('7');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const adminAuth = adminAuthHeader();

  const fetchHealth = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/oscar/health?days=${windowDays}`, { headers: adminAuth });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const empty = !loading && !error && data && data.summary?.total_turns === 0;
  const sum = data?.summary || {};
  const rel = data?.relevance || {};
  const cla = data?.classifier || {};
  const mode = data?.mode_split || {};
  const src = data?.source_split || {};
  const kbUse = data?.kb_use || { v2_eligible_turns: 0, with_kb_hit: 0, without_kb_hit: 0 };
  const totalTurns = sum.total_turns || 0;
  const totalRelevance = Object.values(rel).reduce((a, b) => a + b, 0);
  const totalClassifier = Object.values(cla).reduce((a, b) => a + b, 0);
  const totalMode = Object.values(mode).reduce((a, b) => a + b, 0);
  const totalSrc  = Object.values(src).reduce((a, b) => a + b, 0);
  const dailySeries = data?.daily || [];
  const unknown = data?.unknown_questions || { totals: {}, top_pending: [] };

  return (
    <div data-testid="oscar-subtab-panel-health"
      style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 14,
        background: KB_PANEL_BG, padding: '22px 24px', display: 'flex',
        flexDirection: 'column', gap: 18 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
              Activity & Health
            </h3>
            <StatusBadge>Phase 3C · Live</StatusBadge>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55, maxWidth: 640 }}>
            Read-only telemetry rollup across both Oscar paths. Cost and token figures are
            heuristic estimates from Phase 2E — directional, not provider-authoritative billing.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select data-testid="health-window-select" value={windowDays}
            onChange={e => setWindowDays(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8,
              border: `1px solid ${KB_PANEL_BORDER}`, background: KB_INPUT_BG,
              fontSize: 11, fontWeight: 600, color: DARK, outline: 'none',
              letterSpacing: '0.4px', textTransform: 'uppercase',
              cursor: 'pointer' }}>
            {HEALTH_WINDOWS.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
          <button data-testid="health-refresh" onClick={fetchHealth}
            style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${KB_PANEL_BORDER}`,
              background: '#FFFFFF', color: MUTED, fontSize: 11, fontWeight: 600,
              letterSpacing: '0.4px', textTransform: 'uppercase', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="health-error"
          style={{ padding: '10px 12px', borderRadius: 10,
            background: 'rgba(178,58,72,0.08)', border: '1px solid rgba(178,58,72,0.25)',
            fontSize: 12, color: '#B23A48' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: MUTED, fontSize: 12 }}>
          Loading…
        </div>
      ) : empty ? (
        <div data-testid="health-empty"
          style={{ padding: '40px 24px', textAlign: 'center', color: MUTED,
            border: `1px dashed ${KB_PANEL_BORDER}`, borderRadius: 12,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <ListChecks size={28} color={GOLD} strokeWidth={1.4} />
          <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>
            No Oscar activity yet in this window
          </div>
          <div style={{ fontSize: 12, color: MUTED, maxWidth: 380, lineHeight: 1.55 }}>
            Once guests interact with Oscar (or admins use the v2 preview),
            metrics will surface here. Try widening the time window.
          </div>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <HealthKpi
              testid="kpi-total-turns"
              label="Total Turns"
              value={_fmtInt(sum.total_turns)}
              hint={`over ${data?.window?.days || 7} days · ${_fmtInt(sum.llm_call_count)} LLM calls`}
            />
            <HealthKpi
              testid="kpi-cost"
              label="Approx Cost"
              value={_fmtUsd(sum.approx_cost_usd)}
              hint={`${_fmtInt(sum.approx_tokens_in)} in / ${_fmtInt(sum.approx_tokens_out)} out`}
              approximate
            />
            <HealthKpi
              testid="kpi-kb-hit-rate"
              label="KB Hit Rate"
              value={_fmtPct(sum.kb_hit_rate)}
              hint={`v2-eligible basis: ${_fmtInt(sum.kb_hit_rate_basis)} turns`}
            />
            <HealthKpi
              testid="kpi-escalation-rate"
              label="Escalation Rate"
              value={_fmtPct(sum.escalation_rate)}
              hint="v2 gate label = 'escalate'"
            />
            <HealthKpi
              testid="kpi-latency-p50"
              label="Latency p50"
              value={_fmtMs(sum.p50_latency_ms)}
              hint={`p95 ${_fmtMs(sum.p95_latency_ms)} · n=${sum.latency_sample_size || 0}`}
            />
            <HealthKpi
              testid="kpi-mode-split"
              label="Mode Split"
              value={
                Object.keys(mode).length === 0
                  ? '—'
                  : Object.entries(mode)
                      .map(([k, v]) => `${k} ${v}`)
                      .join(' · ')
              }
              hint={`from oscar_logs.mode field`}
            />
          </div>

          {/* Daily activity */}
          <section data-testid="health-section-daily"
            style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
              background: '#FFFFFF', padding: '14px 16px',
              display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline',
              justifyContent: 'space-between', gap: 10 }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                Daily activity
              </h4>
              <div style={{ display: 'flex', gap: 12, fontSize: 10,
                color: MUTED, letterSpacing: '0.3px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2,
                    background: 'linear-gradient(180deg, #C6A75A, #9D7E3E)' }} /> turns
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: '#D9A24A' }} /> escalated
                </span>
              </div>
            </div>
            <HealthDailyBars daily={dailySeries} />
          </section>

          {/* Two-column: Relevance + KB use */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <section data-testid="health-section-relevance"
              style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
                background: '#FFFFFF', padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                Relevance gate distribution
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['on_topic','off_topic','escalate','n/a'].map(k => {
                  const n = rel[k] || 0;
                  if (n === 0) return null;
                  const color = k === 'on_topic' ? '#1E7344'
                              : k === 'off_topic' ? '#7E8A99'
                              : k === 'escalate' ? '#D9A24A'
                              : '#C6C9CF';
                  return <HealthBar key={k} label={k} count={n} total={totalRelevance} color={color}
                    testid={`health-relevance-${k}`} />;
                })}
              </div>
              <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic',
                paddingTop: 4, borderTop: `1px dashed ${KB_PANEL_BORDER}` }}>
                'n/a' = legacy `/api/chat` turns (no v2 gate ran)
              </div>
            </section>

            <section data-testid="health-section-kb-use"
              style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
                background: '#FFFFFF', padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                KB usage on v2 turns
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <HealthBar label="With KB hit" count={kbUse.with_kb_hit}
                  total={Math.max(1, kbUse.v2_eligible_turns)} color={GOLD}
                  testid="health-kb-with" />
                <HealthBar label="Without KB hit" count={kbUse.without_kb_hit}
                  total={Math.max(1, kbUse.v2_eligible_turns)} color="#7E8A99"
                  testid="health-kb-without" />
              </div>
              <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic',
                paddingTop: 4, borderTop: `1px dashed ${KB_PANEL_BORDER}` }}>
                Computed only over v2 on_topic + escalate turns
                (eligible: {_fmtInt(kbUse.v2_eligible_turns)})
              </div>
            </section>
          </div>

          {/* Two-column: Classifier + Source split */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <section data-testid="health-section-classifier"
              style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
                background: '#FFFFFF', padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                Classifier label distribution
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(cla)
                  .sort(([,a],[,b]) => b - a)
                  .map(([k, v]) => (
                    <HealthBar key={k} label={k} count={v} total={totalClassifier}
                      color={k.startsWith('v2_kb') ? '#1E7344'
                            : k.includes('off_topic') ? '#7E8A99'
                            : k.includes('escalate') ? '#D9A24A'
                            : k.includes('canned') ? '#B23A48'
                            : GOLD}
                      testid={`health-classifier-${k}`} />
                  ))}
              </div>
            </section>

            <section data-testid="health-section-source"
              style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
                background: '#FFFFFF', padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                Source split
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(src)
                  .sort(([,a],[,b]) => b - a)
                  .map(([k, v]) => (
                    <HealthBar key={k} label={k} count={v} total={totalSrc}
                      color={k === 'v2_preview' ? GOLD : '#7E8A99'}
                      testid={`health-source-${k}`} />
                  ))}
              </div>
              <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic',
                paddingTop: 4, borderTop: `1px dashed ${KB_PANEL_BORDER}` }}>
                'legacy_chat' = guest-facing `/api/chat`. 'v2_preview' = admin-only path.
              </div>
            </section>
          </div>

          {/* Unknown questions snapshot */}
          <section data-testid="health-section-unknown"
            style={{ border: `1px solid ${KB_PANEL_BORDER}`, borderRadius: 12,
              background: '#FFFFFF', padding: '14px 16px',
              display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline',
              justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 16, fontWeight: 600, color: DARK }}>
                Unknown questions queue
              </h4>
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: MUTED }}>
                <span>pending <strong style={{ color: DARK }}>{unknown.totals.pending || 0}</strong></span>
                <span>dismissed <strong style={{ color: DARK }}>{unknown.totals.dismissed || 0}</strong></span>
                <span>resolved <strong style={{ color: '#1E7344' }}>{unknown.totals.resolved || 0}</strong></span>
              </div>
            </div>
            {(unknown.top_pending || []).length === 0 ? (
              <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic',
                padding: '6px 0' }}>
                No pending unknown questions.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.8px',
                  textTransform: 'uppercase', color: MUTED }}>
                  Top recurring · pending
                </div>
                {unknown.top_pending.map((u) => (
                  <div key={u.id} data-testid={`health-top-unknown-${u.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 10px', borderRadius: 8,
                      background: 'rgba(217,162,74,0.04)',
                      border: `1px solid ${KB_PANEL_BORDER}` }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                      padding: '2px 7px', borderRadius: 99,
                      background: 'rgba(217,162,74,0.16)',
                      color: '#9D6F1F',
                      border: '1px solid rgba(217,162,74,0.36)' }}>
                      {u.count}× asked
                    </span>
                    <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
                      fontSize: 14, color: DARK, flex: 1, minWidth: 0,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      “{u.question}”
                    </span>
                    <span style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>
                      {u.last_seen ? fmtRelative(u.last_seen) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Pass 4 — Top Off-Topic Patterns. Only renders when at least
              one pattern fired in the window. Sorted by count desc. */}
          {Array.isArray(data?.top_offtopic_patterns) && data.top_offtopic_patterns.length > 0 && (
            <section data-testid="health-top-offtopic-patterns"
              style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between' }}>
                <h4 style={{ margin: 0,
                  fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 16, fontWeight: 600, color: DARK,
                  letterSpacing: '0.2px' }}>
                  Top off-topic patterns
                </h4>
                <span style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>
                  Over the last {data?.window?.days || 7} day{(data?.window?.days || 7) === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.top_offtopic_patterns.slice(0, 10).map((p, idx) => (
                  <div key={`offpat-${idx}`}
                    data-testid={`health-offtopic-pattern-${idx}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 10px', borderRadius: 8,
                      background: 'rgba(0,0,0,0.025)',
                      border: `1px solid ${BORDER}` }}>
                    <span style={{ fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.5px', padding: '2px 7px',
                      borderRadius: 99, background: 'rgba(0,0,0,0.06)',
                      color: DARK, border: '1px solid rgba(0,0,0,0.12)' }}>
                      {p.count}× hit
                    </span>
                    <span style={{ fontFamily: 'Menlo, Consolas, monospace',
                      fontSize: 11, color: DARK, flex: 1, minWidth: 0,
                      whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis' }}>
                      {p.pattern}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Pass 4 — Escalation rate trend. Only renders when both windows
              have data. Surfaces whether escalation rate is trending up or
              down compared to the prior equal-length window. */}
          {data?.escalation_rate_trend?.delta !== null
            && data?.escalation_rate_trend?.delta !== undefined && (
            <section data-testid="health-escalation-trend"
              style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 10,
                background: 'rgba(0,0,0,0.02)',
                border: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                textTransform: 'uppercase', color: MUTED }}>
                Escalation Trend
              </span>
              <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 14, fontWeight: 600, color: DARK }}>
                {(data.escalation_rate_trend.current * 100).toFixed(1)}%
              </span>
              <span style={{ fontSize: 11, color: MUTED }}>
                {(() => {
                  const d = data.escalation_rate_trend.delta;
                  const arrow = d > 0 ? '▲' : (d < 0 ? '▼' : '–');
                  const sign = d > 0 ? '+' : '';
                  return `${arrow} ${sign}${(d * 100).toFixed(1)}% vs prior ${data?.window?.days || 7}d`;
                })()}
              </span>
              <span style={{ fontSize: 11, color: MUTED, fontStyle: 'italic',
                marginLeft: 'auto' }}>
                Prior basis: {data.escalation_rate_trend.prior_basis} turn{data.escalation_rate_trend.prior_basis === 1 ? '' : 's'}
              </span>
            </section>
          )}

          {/* Disclaimer */}
          <div data-testid="health-disclaimer"
            style={{ padding: '10px 12px', borderRadius: 10,
              background: 'rgba(217,162,74,0.05)',
              border: `1px solid ${BORDER_GOLD}`,
              fontSize: 11, color: MUTED, lineHeight: 1.55,
              display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Lock size={12} color={GOLD_DK} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{data?.disclaimer}</span>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Generic "Coming Soon" placeholder for non-live Oscar subtabs ───────────
const OscarComingSoonPanel = ({ subtab }) => {
  const { Icon } = subtab;
  return (
    <div data-testid={`oscar-subtab-panel-${subtab.key}`}
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '40px 28px', minHeight: 260,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 14 }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%',
        background: SOFT_BG, display: 'flex', alignItems: 'center',
        justifyContent: 'center', border: `1px solid ${BORDER}` }}>
        <Icon size={24} color={GOLD} strokeWidth={1.5} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
          fontSize: 22, fontWeight: 600, color: DARK, letterSpacing: '0.3px' }}>
          {subtab.label}
        </h3>
        <StatusBadge tone="muted">Coming Soon</StatusBadge>
      </div>
      <p style={{ margin: 0, maxWidth: 540, fontSize: 13, lineHeight: 1.65, color: MUTED }}>
        {subtab.blurb}
      </p>
      <div style={{ marginTop: 4, fontSize: 11, color: MUTED, fontStyle: 'italic',
        display: 'flex', alignItems: 'center', gap: 6 }}>
        <Lock size={11} /> Arriving {subtab.arriving}.
      </div>
    </div>
  );
};

// ─── Milo Console — Phase 5B holding state ──────────────────────────────────
// Q&A console arrives in a later phase. This is a more intentional holding
// state than the generic "Coming Soon" placeholder: it makes clear that
// Milo will ground its answers in the runbooks authored under the sibling
// Runbooks subtab, and shows live counts so the operator can see grounding
// readiness at a glance.
const MiloConsoleHoldingState = () => {
  const [stats, setStats] = useState({ count: null });
  const [loading, setLoading] = useState(true);
  const [scopeBreakdown, setScopeBreakdown] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/runbooks`, {
          headers: adminAuthHeader(),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        setStats({ count: d.count || 0 });
        const by = {};
        for (const rb of (d.runbooks || [])) {
          by[rb.scope] = (by[rb.scope] || 0) + 1;
        }
        setScopeBreakdown(by);
      } catch (_) {
        if (!cancelled) setStats({ count: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="milo-subtab-panel-console"
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '32px 28px',
        display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12,
          background: 'rgba(198,167,90,0.10)',
          border: `1px solid ${BORDER_GOLD}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0 }}>
          <Terminal size={22} color={GOLD_DK} strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            flexWrap: 'wrap', marginBottom: 4 }}>
            <h3 style={{ margin: 0, fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 700, color: DARK }}>
              Milo Console
            </h3>
            <StatusBadge tone="muted">Q&amp;A · Not yet live</StatusBadge>
            <StatusBadge tone="gold">Grounding ready · Phase 5B</StatusBadge>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: MUTED, lineHeight: 1.65,
            maxWidth: 720 }}>
            The conversational admin console is intentionally not active in this
            phase. When it activates, every answer will be grounded against the
            runbooks authored in <strong style={{ color: DARK }}>Runbooks</strong>
            (the sibling tab) — Milo will not improvise outside that corpus.
          </p>
        </div>
      </div>

      {/* Grounding readiness card */}
      <div data-testid="milo-console-grounding-card"
        style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
          background: '#FFFFFF', padding: '16px 18px',
          display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: 9, fontWeight: 800,
              letterSpacing: '0.10em', textTransform: 'uppercase',
              color: MUTED, display: 'block', marginBottom: 4 }}>
              Grounding corpus
            </span>
            <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 26, fontWeight: 700, color: DARK, lineHeight: 1.05 }}>
              {loading ? '—' : (stats.count ?? 0).toLocaleString()}
            </span>
            <span style={{ fontSize: 11, color: MUTED, marginLeft: 8 }}>
              active runbook{stats.count === 1 ? '' : 's'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic',
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lock size={11} /> Read-only · admin-only
          </div>
        </div>

        {!loading && Object.keys(scopeBreakdown).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {Object.entries(scopeBreakdown)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([scope, n]) => (
                <span key={scope}
                  data-testid={`milo-console-scope-count-${scope}`}
                  style={{ fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    padding: '3px 9px', borderRadius: 99,
                    background: SOFT_BG, color: DARK,
                    border: `1px solid ${BORDER}`,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: MUTED }}>{scope}</span>
                  <strong style={{ color: GOLD_DK }}>{n}</strong>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* What activation will do */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
        background: '#FFFFFF', padding: '16px 18px',
        display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: MUTED }}>
          When live, Milo will…
        </span>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12,
          color: DARK, lineHeight: 1.7 }}>
          <li>Answer admin operational questions <strong>only</strong> from the
            runbook corpus authored in the Runbooks tab.</li>
          <li>Surface the cited runbook(s) with every answer so operators can
            verify the grounding.</li>
          <li>Refuse politely when no runbook covers the question, and route
            the question into a follow-up queue for a runbook author to fill.</li>
          <li>Stay strictly read-only — no writes, no destructive actions.
            Tier-A safe actions arrive in a later phase with explicit
            confirmation.</li>
        </ul>
      </div>

      {/* Boundaries */}
      <div data-testid="milo-console-boundary-note"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 10,
          background: 'rgba(198,167,90,0.05)',
          border: `1px solid ${BORDER_GOLD}` }}>
        <Lock size={13} color={GOLD_DK} style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.6 }}>
          Milo is the operator-side counterpart to Oscar. He is
          <strong style={{ color: DARK }}> not </strong>
          guest-facing. He has no access to guest-facing surfaces or to the
          Oscar Knowledge Base. The two assistants share infrastructure but
          operate on isolated knowledge scopes.
        </div>
      </div>
    </div>
  );
};

export default function AITab({ onNavigate }) {
  // Top-level section: 'oscar' | 'milo'. Default landing per user decision: Oscar.
  const [activeSection, setActiveSection]   = useState('oscar');
  // Per-section nested subtab state. Defaults to first subtab in each list.
  const [oscarSubtab, setOscarSubtab]       = useState('conversations');
  const [miloSubtab,  setMiloSubtab]        = useState('console');

  // Oscar Mode (Feb 2026 reset). Backend is the source of truth; cached
  // here for the header pill + Settings panel. Flipping is admin-only and
  // currently rejects any value other than 'basic' (Enhanced is structurally
  // shown but disabled until v2 ships).
  const [oscarMode, setOscarMode] = useState('basic');
  useEffect(() => {
    fetch(`${API}/api/oscar/mode`, {
      headers: adminAuthHeader(),
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.mode) setOscarMode(d.mode);
    }).catch(() => {});
  }, []);

  // Cross-tab promote-to-KB bootstrap. The Unknown panel sets this and
  // routes the user to the KB subtab; the KB panel reads it on mount and
  // enters compose mode pre-filled. After save, the KB panel marks the
  // unknown question resolved with the resulting fact id.
  const [kbBootstrap, setKbBootstrap] = useState(null);  // { text, unknownId } | null

  // Phase 3D — honor a one-shot deep-link hint set by the Dashboard's Oscar
  // AI widget so clicking the widget lands directly on Activity & Health
  // instead of the default Conversations subtab. Consumed and cleared on mount.
  useEffect(() => {
    try {
      const want = sessionStorage.getItem('ai_default_subtab');
      if (want) {
        sessionStorage.removeItem('ai_default_subtab');
        const valid = OSCAR_SUBTABS.some(s => s.key === want);
        if (valid) {
          setActiveSection('oscar');
          setOscarSubtab(want);
        }
      }
    } catch (_) {}
  }, []);

  const subtabs = activeSection === 'oscar' ? OSCAR_SUBTABS : MILO_SUBTABS;
  const activeSubtab = activeSection === 'oscar' ? oscarSubtab : miloSubtab;
  const setActiveSubtab = activeSection === 'oscar' ? setOscarSubtab : setMiloSubtab;
  const current = subtabs.find(s => s.key === activeSubtab) || subtabs[0];

  return (
    // data-testid="oscar-hub-tab" retained for back-compat with previous test harness;
    // new "ai-tab" is the canonical handle going forward.
    <div data-testid="ai-tab"
      style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 28px 40px' }}>
      <div data-testid="oscar-hub-tab" style={{ display: 'contents' }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <AdminPageHeader
        eyebrow="Intelligence"
        title="AI"
        tagline="Single home for the assistant system. Two distinct AIs with separate permissions, knowledge scopes, and behavioral guardrails."
        right={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <StatusBadge>Phase 1 · Foundation</StatusBadge>
            {/* Oscar Mode pill — always visible inside the AI tab so the
                operating mode is unambiguous at a glance. */}
            <span data-testid="ai-mode-pill"
              style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
                textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
                background: 'rgba(30,115,68,0.10)', color: '#1E7344',
                border: '1px solid rgba(30,115,68,0.28)' }}>
              Mode: {oscarMode === 'basic' ? 'Basic' : 'Enhanced'}
            </span>
          </div>
        )}
      />

      {/* ── Status banner ────────────────────────────────────────── */}
      <div data-testid="ai-status-banner"
        style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
          background: SOFT_BG, padding: '14px 18px', marginBottom: 24,
          display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <Lock size={16} color={GOLD_DK} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 4 }}>
            Foundation layer in place. No AI behavior change yet.
          </p>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
            The current Oscar guest widget continues to use the legacy chat endpoint untouched.
            Oscar v2 (relevance gate · KB injection · proper logging · luxury concierge persona)
            arrives in <strong style={{ color: GOLD_DK }}>Phase 2</strong>.
            Milo (admin/operator assistant) arrives in <strong style={{ color: GOLD_DK }}>Phase 5+</strong>.
          </p>
        </div>
      </div>

      {/* ── Top-level section nav: Oscar | Milo ──────────────────── */}
      <div role="tablist"
        style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        {[
          { id: 'oscar', label: 'Oscar', sub: 'Guest-facing concierge', Icon: Sparkles },
          { id: 'milo',  label: 'Milo',  sub: 'Admin/operator assistant', Icon: Bot },
        ].map(sec => {
          const active = sec.id === activeSection;
          const Icon = sec.Icon;
          return (
            <button
              key={sec.id}
              role="tab"
              aria-selected={active}
              onClick={() => setActiveSection(sec.id)}
              data-testid={`ai-section-${sec.id}`}
              style={{ flex: '1 1 0', display: 'flex', alignItems: 'center',
                gap: 12, padding: '14px 18px', cursor: 'pointer',
                background: active ? '#FFFFFF' : SOFT_BG,
                border: `1.5px solid ${active ? GOLD : BORDER}`,
                borderRadius: 12, transition: 'all 0.18s ease',
                boxShadow: active ? '0 2px 12px rgba(198,167,90,0.18)' : 'none' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10,
                background: active ? `linear-gradient(135deg, ${GOLD}, ${GOLD_DK})` : 'rgba(198,167,90,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={18} color={active ? '#1B1812' : GOLD_DK} strokeWidth={1.8} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: DARK,
                  letterSpacing: '0.2px' }}>{sec.label}</div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sec.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Oscar Live Mode block (master control) ───────────────── */}
      {activeSection === 'oscar' && (
        <OscarLiveModeBlock oscarMode={oscarMode} onModeChange={setOscarMode} />
      )}

      {/* ── Nested subtab nav ────────────────────────────────────── */}
      <div role="tablist" style={{ display: 'flex', gap: 6,
        marginBottom: 18, borderBottom: `1px solid ${BORDER}`,
        paddingBottom: 0, flexWrap: 'wrap' }}>
        {subtabs.map(s => {
          const active = s.key === activeSubtab;
          // Keep legacy oscar-subtab-* testids when in Oscar section so the
          // existing test harness/regression checks continue to work.
          const testid = activeSection === 'oscar'
            ? `oscar-subtab-${s.key}`
            : `milo-subtab-${s.key}`;
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={active}
              onClick={() => setActiveSubtab(s.key)}
              data-testid={testid}
              style={{ position: 'relative', background: 'transparent',
                border: 'none', padding: '10px 14px', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: active ? GOLD_DK : MUTED,
                borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                marginBottom: -1, transition: 'color 0.15s ease' }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* ── Active subtab content ────────────────────────────────── */}
      {(() => {
        if (activeSection === 'oscar') {
          if (activeSubtab === 'conversations') return <ConversationsPanel />;
          if (activeSubtab === 'kb')            return <OscarKnowledgeBasePanel
            kbBootstrap={kbBootstrap}
            onBootstrapConsumed={() => setKbBootstrap(null)} />;
          // Pass 7 / O2 — General guidance management surface.
          if (activeSubtab === 'guidance')      return <OscarGuidancePanel />;
          if (activeSubtab === 'unknown')       return <OscarUnknownPanel
            onPromoteToKb={(text, unknownId) => {
              setKbBootstrap({ text, unknownId });
              setOscarSubtab('kb');
            }} />;
          if (activeSubtab === 'settings')      return <OscarSettingsPanel oscarMode={oscarMode} />;
          if (activeSubtab === 'health')        return <OscarHealthPanel />;
          return <OscarComingSoonPanel subtab={current} />;
        }
        // Milo section
        if (activeSubtab === 'runbooks') return <MiloRunbooksPanel />;
        if (activeSubtab === 'console')  return <MiloConsolePanel
          onOpenRunbook={(familyId) => {
            try { sessionStorage.setItem('milo_runbook_open_family_id', familyId); } catch (_) {}
            setMiloSubtab('runbooks');
          }}
          onNavigate={onNavigate} />;
        if (activeSubtab === 'actionlog') return <MiloActionLogPanel />;
        if (activeSubtab === 'settings')  return <MiloSettingsPanel />;
        // Pass 6 / M17
        if (activeSubtab === 'boundaries') return <MiloBoundariesPanel />;
        return <SubtabPanel subtab={current} />;
      })()}

      </div>
    </div>
  );
}
